/* eslint-disable no-console */
import PropTypes from 'prop-types';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Box, Typography, Avatar, IconButton, ClickAwayListener, Popper, Grow, Paper, Skeleton, Fade, Backdrop, CircularProgress, Button, Dialog, DialogContent, DialogActions, TextField, Tooltip, Chip } from '@mui/material';
import { Iconify } from 'src/components';
import TreatmentFlowComponent from 'src/components/treatmentComponents/TreatmentFlowComponent';
import TreatmentEditPopover from 'src/pages/dashboard/tables/AllLeadTable/TreatmentEditPopover';
import { dispatch, useSelector } from 'src/redux/store';
import { updateAssignToUser, addNotes, updateNotes, UpdateTreatmentV3, addTreatment, addAppointment, saveAIBookingPayment } from 'src/redux/slices/lead';
import { createSubmission, updateSubmission, getCallScriptSection, setCallScriptSection } from 'src/redux/slices/callScript';
import { getPracticeUserListing, clearPracticeUserData } from 'src/redux/slices/practiceUser';
import { getTemplateList } from 'src/redux/slices/practiceMessage';
import { getWhatsappAccount } from 'src/redux/slices/whatsappAccount';
import { get10WpTemplateList } from 'src/redux/slices/whatsAppMessages';
import { getAllTagItem, createTagItem, createCustomTagItem } from 'src/redux/slices/tagsCreate';
import { fetchAiConfig } from 'src/redux/slices/whatsappV2';
import { AssignTaskPopup } from 'src/components/dialogPopUp';
import IosEmailComposer from 'src/sections/tasks/composers/IosEmailComposer';
import IosWhatsAppComposerDialog from 'src/sections/tasks/composers/IosWhatsAppComposerDialog';
import useAuth from 'src/hooks/useAuth';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import FeatureGuard from 'src/guards/FeatureGuard';
import { saveDraft, deleteDraft, generateDraftId, cleanupExpiredDrafts } from 'src/utils/callScriptDraftDB';
import { resolveActiveTreatment } from 'src/utils/activeTreatment';
import { getFirstConnectedWhatsappBusinessAccount } from 'src/utils/whatsappAccountLifecycle';
import { CANONICAL_SALUTATION_VALUES } from 'src/utils/getReferralData';

import { transformScriptData } from './utils';
import { generateAICallSummary } from './aiCallScriptService';

import {
  CallScriptHeader,
  SectionTabs,
  QuestionProgress,
  ScriptCard,
  StickyFooter,
  ContextPanel,
  SayBlock,
  TipBlock,
  SectionDivider,
  NoteToggle,
  FormRow,
  ScriptTextField,
  ScriptSelect,
  ScriptTextarea,
  ScriptDateField,
  ChoiceGrid,
  ResponseButtons,
  InterestCards,
  OutcomeButtons,
  ScaleRating,
  LeadSearchField,
  HintAlert,
  AISummaryPrompt,
  AISummaryModal,
  AIBookingWizard,
  CaptureContactDetailsLive,
  ConfirmSpeakerLive,
  CompletedBySelector,
  ReasonForCallingLive,
  IdentifyCallerLive,
  CloseCallLive,
  IdentifyCaller,
  ReasonForCalling,
  CloseCall,
  CoachingBlock,
  HintBlocks,
  DraftRecoveryBanner,
} from './components';

import { appContainerSx, mainContainerSx, scriptPanelSx, cardAreaSx, scrollableCardAreaSx, noteToggleSx } from './styles';

/**
 * Helper to safely join name parts
 */
const safeJoinName = (firstName, lastName) => {
  const parts = [firstName, lastName].filter(Boolean);
  return parts.join(' ') || '';
};

/**
 * Helper to get treatment name from treatment object
 */
const getTreatmentName = (treatment) => {
  if (!treatment) return '';
  if (typeof treatment === 'string') return treatment;
  return treatment.name || treatment.treatment_name || '';
};

/**
 * Resolve the treatment this call script should act on, given a lead and the
 * active treatment id (from the multiTreatment bridge). Returns the active
 * treatment when it belongs to the lead, else the most-recently-updated
 * selectable treatment; only falls back to raw lead_treatments[0] when nothing
 * is selectable (e.g. an all-closed edge case). Pure — the caller passes
 * activeTreatmentId so the component derives this reactively (useSelector + useMemo).
 */
const resolveCallScriptTreatment = (lead, activeTreatmentId) =>
  resolveActiveTreatment(lead, activeTreatmentId) || lead?.lead_treatments?.[0] || null;

/**
 * Helper to evaluate a single conditional logic condition
 * @param {Object} condition - { field: number, operator: string, value: string }
 * @param {Object} formData - Current form data
 * @param {Object} fieldIdToNameMap - Map of field IDs to field names
 * @returns {boolean} Whether the condition is satisfied
 */
const evaluateCondition = (condition, formData, fieldIdToNameMap) => {
  const { field: fieldId, operator, value: conditionValue } = condition;

  // Find the field name from the field ID
  const fieldName = fieldIdToNameMap[fieldId];
  if (!fieldName) return false;

  // Get the current value from form data
  const currentValue = formData[fieldName];

  // Normalize values for comparison
  const normalizeValue = (val) => {
    if (val === null || val === undefined) return '';
    if (Array.isArray(val)) {
      // For arrays (multi-select), join values
      return val.map(v => String(v).toLowerCase().trim()).join(' | ');
    }
    return String(val).toLowerCase().trim();
  };

  const current = normalizeValue(currentValue);
  const expected = String(conditionValue || '').toLowerCase().trim();

  // Also check if any array item matches for IS/IS_NOT
  const currentArray = Array.isArray(currentValue)
    ? currentValue.map(v => String(v).toLowerCase().trim())
    : [current];

  switch (operator) {
    case 'IS':
    case 'EQUALS':
      // For arrays, check if expected value is in the array
      return currentArray.some(v => v === expected) || current === expected;
    case 'IS_NOT':
    case 'NOT_EQUALS':
      return !currentArray.some(v => v === expected) && current !== expected;
    case 'CONTAINS':
      return current.includes(expected);
    case 'NOT_CONTAINS':
      return !current.includes(expected);
    case 'STARTS_WITH':
      return current.startsWith(expected);
    case 'ENDS_WITH':
      return current.endsWith(expected);
    case 'GT':
      return parseFloat(current) > parseFloat(expected);
    case 'GTE':
      return parseFloat(current) >= parseFloat(expected);
    case 'LT':
      return parseFloat(current) < parseFloat(expected);
    case 'LTE':
      return parseFloat(current) <= parseFloat(expected);
    default:
      return false;
  }
};

/**
 * Helper to evaluate conditional logic for a field
 * @param {Object} logic - Conditional logic rule
 * @param {Object} formData - Current form data
 * @param {Object} fieldIdToNameMap - Map of field IDs to field names
 * @returns {boolean} Whether the field should be visible
 */
const evaluateConditionalLogic = (logic, formData, fieldIdToNameMap) => {
  if (!logic || !logic.conditions || logic.conditions.length === 0) {
    return true; // No conditions = always visible
  }

  const { action, action_operator_type: operatorType, conditions } = logic;

  // Evaluate all conditions
  const results = conditions.map(condition =>
    evaluateCondition(condition, formData, fieldIdToNameMap)
  );

  // Determine if conditions are met based on operator type
  const conditionsMet = operatorType === 'ALL'
    ? results.every(r => r) // ALL conditions must match
    : results.some(r => r);  // ANY condition must match

  // Determine visibility based on action
  if (action === 'SHOW') {
    return conditionsMet; // Show if conditions are met
  }
  // HIDE action
  return !conditionsMet; // Hide if conditions are met (show if not met)
};

/**
 * Helper to evaluate a single metadata-based condition
 * @param {Object} condition - { fieldId: number, operator: string, value: string | array, isSubField?: boolean, subFieldName?: string }
 * @param {Object} formData - Current form data
 * @param {Object} fieldIdToNameMap - Map of field IDs to field names
 * @returns {boolean} Whether the condition is satisfied
 */
const evaluateMetadataCondition = (condition, formData, fieldIdToNameMap) => {
  const { fieldId, operator, value: conditionValue, isSubField, subFieldName } = condition;

  // Field must have an ID to be evaluated (only saved fields)
  if (!fieldId) return false;

  // For sub-fields (special composite fields), use the subFieldName directly
  // Sub-field names like 'callerType', 'reasonForCall', 'interestLevel' are stored directly in formData
  let fieldName;
  if (isSubField && subFieldName) {
    fieldName = subFieldName;
  } else {
    // Find the field name from the field ID
    fieldName = fieldIdToNameMap[fieldId];
    if (!fieldName) return false;
  }

  // Get the current value from form data
  const currentValue = formData[fieldName];

  // Normalize values for comparison
  const normalizeValue = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'boolean') return val ? 'yes' : 'no';
    if (Array.isArray(val)) {
      return val.map(v => String(v).toLowerCase().trim());
    }
    return String(val).toLowerCase().trim();
  };

  const current = normalizeValue(currentValue);
  const expected = Array.isArray(conditionValue)
    ? conditionValue.map(v => String(v).toLowerCase().trim())
    : String(conditionValue || '').toLowerCase().trim();

  // Handle array values (multi-select fields)
  const currentIsArray = Array.isArray(current);
  const expectedIsArray = Array.isArray(expected);

  switch (operator) {
    case 'equals':
      if (currentIsArray && expectedIsArray) {
        // Both are arrays - exact match (same items, same order not required)
        if (current.length !== expected.length) return false;
        return expected.every(v => current.includes(v));
      }
      if (currentIsArray) {
        // Current is array, expected is single value - check if array has exactly one matching item
        return current.length === 1 && current[0] === expected;
      }
      return current === expected;

    case 'not_equals':
      if (currentIsArray && expectedIsArray) {
        // Not exact match
        if (current.length !== expected.length) return true;
        return !expected.every(v => current.includes(v));
      }
      if (currentIsArray) {
        return !(current.length === 1 && current[0] === expected);
      }
      return current !== expected;

    case 'contains':
      if (currentIsArray) {
        // At least one value matches
        if (expectedIsArray) {
          return expected.some(v => current.includes(v));
        }
        return current.includes(expected);
      }
      // String contains
      return current.includes(expected);

    case 'not_contains':
      if (currentIsArray) {
        // None of the values match
        if (expectedIsArray) {
          return !expected.some(v => current.includes(v));
        }
        return !current.includes(expected);
      }
      // String not contains
      return !current.includes(expected);

    case 'is_empty':
      if (currentIsArray) return current.length === 0;
      return current === '';

    case 'is_not_empty':
      if (currentIsArray) return current.length > 0;
      return current !== '';

    default:
      return false;
  }
};

/**
 * Helper to evaluate metadata-based conditional logic (new feature)
 * This works alongside the existing conditionalLogics system
 *
 * Expected metadata.conditionLogic structure:
 * {
 *   action: 'show' | 'hide',
 *   logicType: 'AND' | 'OR',
 *   conditions: [
 *     { fieldId: 123, fieldLabel: 'Field Name', operator: 'equals', value: 'Yes' }
 *   ]
 * }
 *
 * @param {Object} conditionLogic - The conditionLogic object from metadata
 * @param {Object} formData - Current form data
 * @param {Object} fieldIdToNameMap - Map of field IDs to field names
 * @returns {boolean} Whether the field should be visible
 */
const evaluateMetadataConditionLogic = (conditionLogic, formData, fieldIdToNameMap) => {
  if (!conditionLogic || !conditionLogic.conditions || conditionLogic.conditions.length === 0) {
    return true; // No conditions = always visible
  }

  const { action = 'show', logicType = 'AND', conditions } = conditionLogic;

  // Evaluate all conditions
  const results = conditions.map(condition =>
    evaluateMetadataCondition(condition, formData, fieldIdToNameMap)
  );

  // Determine if conditions are met based on logic type
  const conditionsMet = logicType.toUpperCase() === 'AND'
    ? results.every(r => r) // ALL conditions must match
    : results.some(r => r);  // ANY condition must match

  // Determine visibility based on action
  if (action.toLowerCase() === 'show') {
    return conditionsMet; // Show if conditions are met
  }
  // 'hide' action
  return !conditionsMet; // Hide if conditions are met (show if not met)
};

/**
 * Helper to format condition for display in preview mode
 * @param {Object} conditionLogic - The conditionLogic object from metadata
 * @returns {string} Human-readable condition description
 */
const formatConditionLogicForPreview = (conditionLogic) => {
  if (!conditionLogic || !conditionLogic.conditions || conditionLogic.conditions.length === 0) {
    return '';
  }

  const { action = 'show', logicType = 'AND', conditions } = conditionLogic;
  const actionText = action.toLowerCase() === 'show' ? 'Shown' : 'Hidden';
  const logicText = logicType.toUpperCase() === 'AND' ? 'all' : 'any';

  const conditionTexts = conditions.map(cond => {
    const fieldLabel = cond.fieldLabel || `Field ${cond.fieldId}`;
    const value = Array.isArray(cond.value) ? cond.value.join(', ') : cond.value;

    switch (cond.operator) {
      case 'equals':
        return `"${fieldLabel}" equals "${value}"`;
      case 'not_equals':
        return `"${fieldLabel}" does not equal "${value}"`;
      case 'contains':
        return `"${fieldLabel}" contains "${value}"`;
      case 'not_contains':
        return `"${fieldLabel}" does not contain "${value}"`;
      case 'is_empty':
        return `"${fieldLabel}" is empty`;
      case 'is_not_empty':
        return `"${fieldLabel}" is not empty`;
      default:
        return `"${fieldLabel}" ${cond.operator} "${value}"`;
    }
  });

  if (conditions.length === 1) {
    return `${actionText} when ${conditionTexts[0]}`;
  }

  return `${actionText} when ${logicText} of: ${conditionTexts.join('; ')}`;
};

/**
 * Helper to extract $Hint: patterns from text
 * Returns { text: string (without hint), hint: string | null }
 */
const extractHint = (text) => {
  if (!text || typeof text !== 'string') return { text, hint: null };

  // Match $Hint: followed by the message (until end of string or next $ pattern)
  const hintRegex = /\$Hint:\s*(.+?)(?=\$[A-Za-z]|$)/gi;
  const matches = text.match(hintRegex);

  if (!matches || matches.length === 0) {
    return { text, hint: null };
  }

  // Extract the hint message
  let hint = matches[0].replace(/\$Hint:\s*/i, '').trim();
  // Clean up any escaped characters
  hint = hint.replace(/\\s/g, ' ').replace(/\\/g, '');

  // Remove the hint from the original text
  const cleanText = text.replace(hintRegex, '').trim();

  return { text: cleanText, hint };
};

/**
 * Hint type toggles — agents can hide coaching / sales / empathy hints during a call to
 * declutter the question card. Persisted per-user per browser via localStorage so the
 * preference follows each agent across every script and reload without leaking between
 * accounts on a shared workstation.
 */
const HINT_TOGGLE_TYPES = ['coaching', 'sales', 'empathy'];

const buildHintToggleStorageKey = (userIdentifier) =>
  `callscript:hiddenHintTypes:${userIdentifier || 'guest'}`;

const readHiddenHintTypes = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((t) => HINT_TOGGLE_TYPES.includes(t));
  } catch {
    return [];
  }
};

const writeHiddenHintTypes = (key, arr) => {
  try {
    localStorage.setItem(key, JSON.stringify(arr));
  } catch {
    // Storage unavailable — accept transient state, don't crash.
  }
};

/**
 * CallScriptV2 - New modular call script UI
 *
 * This component provides a modern, card-based call script interface
 * with section navigation, question progress tracking, and a context panel.
 *
 * @param {boolean} previewMode - When true, disables all save/API functionality
 *   and shows placeholder data for lead-dependent fields. Use this for previewing
 *   call scripts at the practice level without a lead context.
 * @param {string} scriptCategory - The category of the script ('INBOUND', 'OUTBOUND', 'TCO').
 *   Used to determine outcome button options:
 *   - INBOUND/TCO: Only "Submit" button
 *   - OUTBOUND: 2 buttons (Not Booked, Booked)
 */
const CallScriptV2 = ({
  scriptData = {},
  leadData = {},
  followLead = null,
  type = 'inbound',
  onClose,
  onSave,
  onComplete,
  onAppointmentBooked,
  onAppointmentNotBooked,
  onUnableToContact,
  previewMode = false,
  scriptCategory = 'INBOUND',
  onCardChange,
  navigateTo = null,
  scrollableView = false,
  simulatedTreatment = null,
  onClearSimulatedTreatment = null,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  // Redux selectors for merge tags, practiceId, and salutation options
  const mergeTags = useSelector((state) => state.mergeTags);
  const { practiceId } = useSelector((state) => state.practiceTreatmentList);
  // Active treatment selected on the lead-detail page, mirrored into multiTreatment.
  // Read reactively so the script's treatment fields recompute if it changes.
  const { currentTreatmentDataGlobal } = useSelector((state) => state.multiTreatment);
  const activeTreatmentId = currentTreatmentDataGlobal?.treatmentData?.id;
  const activeCallScriptTreatment = useMemo(
    () => resolveCallScriptTreatment(followLead, activeTreatmentId),
    [followLead, activeTreatmentId]
  );
  const { salutationOptions, appointmentMethod, appointmentType, sourceType, referral: referralOptions } = useSelector((state) => state.schema);
  const { practiceUser } = useSelector((state) => state.practiceUser);
  // Treatment-based script merging: fetched scripts matching lead's treatment
  // callScriptReusableData contains ALL scripts for precomputation (avoids unnecessary API calls)
  // inboundScriptsData contains INBOUND scripts prefetched at dashboard load
  const { callScriptData, callScriptReusableData, inboundScriptsData } = useSelector((state) => state.callScript);
  // Templates for AI recommendations
  const { templateDetails: emailTemplates } = useSelector((state) => state.practiceMessage);
  const { wpTemplateListGlobal: whatsappTemplates } = useSelector((state) => state.whatsappMessages);
  const { whatsappAccountData } = useSelector((state) => state.whatsappAccount);
  // Tags for AI summary
  const { getAllTag: existingTags } = useSelector((state) => state.tagsCreate);
  const { aiConfig } = useSelector((state) => state.whatsappV2);
  // Practice choices for reason for calling dropdown
  const choicesData = useSelector((state) => state.practiceTreatment?.choicesData?.results || []);

  // Extract "Practice Reasons For Calling" options from choicesData
  const reasonForCallingOptions = useMemo(() => {
    const reasonChoice = choicesData.find((c) =>
      c?.name?.toLowerCase().includes('practice reasons for calling') ||
      c?.name?.toLowerCase() === 'reasons for calling'
    );
    if (!reasonChoice?.items) return [];
    return reasonChoice.items.map((item) => ({
      value: item.value || item.name || '',
      label: item.value || item.name || '',
      colour: item.colour || item.color || '#637381',
    }));
  }, [choicesData]);

  // Check if AI Call Summary feature is explicitly enabled in Practice Choices
  const isAiCallSummaryEnabled = Boolean(aiConfig?.enableAiCallSummary === true);

  // Get current user from auth context
  const { user: currentAuthUser } = useAuth();

  // State
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [formData, setFormData] = useState({});
  const [completedCards, setCompletedCards] = useState(new Set());
  const [autoFilledCards, setAutoFilledCards] = useState(new Set());
  const [skippedCards, setSkippedCards] = useState(new Set());
  const [cardNotes, setCardNotes] = useState({});
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error
  const [contextPanelOpen, setContextPanelOpen] = useState(false);
  const [callNotes, setCallNotes] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [aiSummaryModalOpen, setAiSummaryModalOpen] = useState(false);
  const [aiSummaryData, setAiSummaryData] = useState(null);

  // AI Booking Wizard state
  const [aiBookingWizardOpen, setAiBookingWizardOpen] = useState(false);
  const [aiBookingLoading, setAiBookingLoading] = useState(false);
  const [appointmentBooked, setAppointmentBooked] = useState(false);
  const [bookedAppointmentData, setBookedAppointmentData] = useState(null);

  // Call timer tracking
  const [callStartTime] = useState(() => new Date());
  const [callDuration, setCallDuration] = useState(null);

  // Treatment-based script merging state
  const [additionalScriptCards, setAdditionalScriptCards] = useState([]);
  const [additionalScriptSections, setAdditionalScriptSections] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [selectedTemplateId, setSelectedTemplateId] = useState(null); // Reserved for future template selection dropdown
  const [treatmentScriptsLoaded, setTreatmentScriptsLoaded] = useState(false);
  // For OUTBOUND: when treatment changes, the entire base script is replaced
  const [overrideBaseScript, setOverrideBaseScript] = useState(null);
  // Treatment switching modal state
  const [treatmentSwitchingModal, setTreatmentSwitchingModal] = useState({ open: false, message: '' });
  // Ref to track previous script ID for comparison (to avoid showing modal when script doesn't change)
  const previousScriptIdRef = useRef(null);

  // Special field state
  const [assignToState, setAssignToState] = useState([]);
  const [treatmentPopoverOpen, setTreatmentPopoverOpen] = useState(false);
  const [treatmentAnchorEl, setTreatmentAnchorEl] = useState(null);
  const treatmentButtonRef = useRef(null);
  const [userAutoSelected, setUserAutoSelected] = useState(false);
  const [assigneePopupOpen, setAssigneePopupOpen] = useState(false);
  const [assigneeAnchorEl, setAssigneeAnchorEl] = useState(null);
  const autoSaveTimeoutRef = useRef(null);
  const pendingChangesRef = useRef({});
  // Refs for scrollable view - stores ref for each card by its global index
  const scrollableCardRefs = useRef({});
  const scrollableAreaRef = useRef(null);
  // Ref for tracking script submission state (like original implementation)
  // fieldSubmissionMap tracks field ID -> submission entry ID for PATCH calls
  const formRef = useRef({ inProgress: false, formId: null, fieldMap: {}, fieldSubmissionMap: {}, pendingFormData: null });
  // Ref to track if treatment was user-changed (to prevent initial useEffect from re-fetching stale treatment scripts)
  const treatmentUserChangedRef = useRef(false);
  // Ref to skip script processing when treatment changes but script remains the same (avoids unnecessary step reset)
  const skipScriptProcessingRef = useRef(false);
  // Ref to track which treatment the script fetch was for (to discard stale responses — Priority 13 fix)
  const requestedTreatmentRef = useRef(null);
  // Ref for tracking notes state to avoid duplicate note creation
  const notesRef = useRef({ noteId: null, lastSavedContent: '' });
  // Ref to access current formData in effects without adding it as a dependency
  const formDataRef = useRef({});
  formDataRef.current = formData;
  // Ref for IdentifyCallerLive to handle auto-create on navigation
  const identifyCallerRef = useRef(null);

  // ── Draft auto-save (IndexedDB) ──
  const draftIdRef = useRef(null);
  const draftSaveWarnedRef = useRef(false);
  // Generate a unique draft ID once practiceId is available (useEffect ensures it runs even if practiceId loads late)
  useEffect(() => {
    if (!draftIdRef.current && !previewMode && practiceId) {
      draftIdRef.current = generateDraftId(practiceId, scriptCategory);
    }
  }, [practiceId, scriptCategory, previewMode]);
  // Cleanup expired drafts once on mount
  useEffect(() => {
    if (!previewMode) cleanupExpiredDrafts();
  }, [previewMode]);

  // Save current form state to IndexedDB draft (called from debouncedAutoSave)
  const saveDraftToIndexedDB = useCallback(async () => {
    if (previewMode || !draftIdRef.current) return;
    const {current} = formDataRef;
    const callerName = safeJoinName(current.callerFirstName || current.firstName, current.callerLastName || current.lastName);
    const ok = await saveDraft({
      draftId: draftIdRef.current,
      practiceId,
      scriptCategory,
      scriptName: scriptData?.title || '',
      scriptId: scriptData?.id || null,
      callerName: callerName || 'Unknown caller',
      leadId: current.lead_id || followLead?.id || null,
      formData: current,
      cardNotes,
      callNotes,
      currentCardIndex,
    });
    // If IndexedDB is unavailable (incognito, quota exceeded), log once
    if (ok === false && !draftSaveWarnedRef.current) {
      draftSaveWarnedRef.current = true;
      console.warn('callScriptDraftDB: Local draft save unavailable — drafts will not persist');
    }
  }, [practiceId, scriptCategory, scriptData?.title, scriptData?.id, followLead?.id, cardNotes, callNotes, currentCardIndex, previewMode]);

  // Handle restoring a draft
  const handleRestoreDraft = useCallback((draft) => {
    if (!draft?.formData) return;
    setFormData(draft.formData);
    if (draft.currentCardIndex != null) {
      setCurrentCardIndex(draft.currentCardIndex);
    }
    if (draft.cardNotes) {
      setCardNotes(draft.cardNotes);
    }
    if (draft.callNotes) {
      setCallNotes(draft.callNotes);
    }
    enqueueSnackbar('Draft restored successfully', { variant: 'success', autoHideDuration: 3000 });
  }, [enqueueSnackbar]);

  // Delete draft on successful completion
  const clearCurrentDraft = useCallback(() => {
    if (draftIdRef.current) {
      deleteDraft(draftIdRef.current);
    }
  }, []);

  // Confirm speaker flow state (for outbound)
  // showWrongPersonModal: displays a modal for adding notes when "Wrong person" is selected
  const [showWrongPersonModal, setShowWrongPersonModal] = useState(false);
  const [wrongPersonNote, setWrongPersonNote] = useState('');

  // Per-question Send Email / Send WhatsApp composers (only used during a live call,
  // not in editor preview). Single shared instance — opening from any card uses the
  // same lead context.
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);
  const [whatsappComposerOpen, setWhatsappComposerOpen] = useState(false);

  // Hidden hint types — array subset of HINT_TOGGLE_TYPES. Persisted per-user in
  // localStorage so each agent's preference follows them across scripts and page reloads
  // without leaking between accounts on a shared workstation.
  const hintToggleStorageKey = buildHintToggleStorageKey(currentAuthUser?.email || currentAuthUser?.id);
  const [hiddenHintTypes, setHiddenHintTypes] = useState(() => readHiddenHintTypes(hintToggleStorageKey));
  const toggleHintType = useCallback((type) => {
    setHiddenHintTypes((prev) => {
      const next = prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type];
      writeHiddenHintTypes(hintToggleStorageKey, next);
      return next;
    });
  }, [hintToggleStorageKey]);

  // Build merge tag data from followLead and Redux merge tags
  // In preview mode, use placeholder values to show what merge tags will look like
  const mergeTagData = useMemo(() => {
    // Preview mode: return empty object so merge tags show as placeholders
    if (previewMode) {
      return {};
    }

    const data = {};

    // Add data from followLead
    if (followLead) {
      data.lead_first_name = followLead.first_name || '';
      data.lead_last_name = followLead.last_name || '';
      data.lead_full_name = safeJoinName(followLead.first_name, followLead.last_name);
      data.lead_email = followLead.lead_contact?.email || '';
      data.lead_phone = followLead.lead_contact?.phone || '';
      data.lead_salutation = followLead.salutation || '';

      // Treatment data — follow the active/selected treatment, not raw [0].
      const primaryTreatment = activeCallScriptTreatment;
      if (primaryTreatment) {
        data.treatment_name = getTreatmentName(primaryTreatment.treatment);
        data.treatment_price = primaryTreatment.price || '';
      }
    }

    // Always add practice and user merge tags (they're not lead-specific)
    if (mergeTags?.mtValPractice) {
      Object.assign(data, mergeTags.mtValPractice);
    }
    if (mergeTags?.mtValUser) {
      Object.assign(data, mergeTags.mtValUser);
    }

    // Add data from formData LAST (highest priority - user's current selection)
    // This ensures manually selected lead/treatment overrides any stale data
    if (formData.firstName) {
      data.lead_first_name = formData.firstName;
    }
    if (formData.lastName) {
      data.lead_last_name = formData.lastName;
    }
    if (formData.firstName || formData.lastName) {
      data.lead_full_name = safeJoinName(formData.firstName, formData.lastName);
    }
    if (formData.treatment) {
      data.treatment_name = formData.treatment;
    }
    if (formData.treatmentValue) {
      data.treatment_price = formData.treatmentValue;
    }
    if (formData.email) {
      data.lead_email = formData.email;
    }
    if (formData.phone || formData.phone_number) {
      data.lead_phone = formData.phone || formData.phone_number;
    }

    return data;
  }, [followLead, formData, mergeTags, previewMode, activeCallScriptTreatment]);

  // Mark scripts as loaded when component receives scriptData via props (e.g., INBOUND from OpenCallScriptPopup)
  // This prevents the skeleton from showing when the script is already loaded via props
  // BUT: Don't interfere when user is actively changing treatments (treatmentUserChangedRef is true)
  useEffect(() => {
    if (scriptData?.treatments?.length > 0 && !treatmentScriptsLoaded && !treatmentUserChangedRef.current) {
      setTreatmentScriptsLoaded(true);
    }
  }, [scriptData?.treatments?.length, treatmentScriptsLoaded]);

  // Initialize form data from followLead (auto-fill)
  useEffect(() => {
    if (followLead) {
      const acsTreatment = activeCallScriptTreatment;
      const initialData = {
        lead_id: followLead.id || '',
        firstName: followLead.first_name || '',
        lastName: followLead.last_name || '',
        leadName: `${followLead.first_name || ''} ${followLead.last_name || ''}`.trim(),
        title: followLead.salutation || '',
        phone: followLead.lead_contact?.phone || '',
        phone_number: followLead.lead_contact?.phone || '',
        email: followLead.lead_contact?.email || '',
        treatment: getTreatmentName(acsTreatment?.treatment),
        treatmentValue: acsTreatment?.price || '',
        treatment_id: acsTreatment?.id || '',
        teethAmount: acsTreatment?.leadtreatment_specification?.teeth_count ?? null,
        // Treatment interest fields (for reason_for_calling attribute)
        treatmentInterest: getTreatmentName(acsTreatment?.treatment),
        treatmentInterestValue: acsTreatment?.price || '',
        treatmentInterestTeeth: acsTreatment?.leadtreatment_specification?.teeth_count ?? null,
        dob: followLead.dob || followLead.date_of_birth || '',
        date_of_birth: followLead.date_of_birth || followLead.dob || '',
        // Address fields (from lead_address, not lead_contact)
        address_line_1: followLead.lead_address?.address_line_1 || '',
        address_line_2: followLead.lead_address?.address_line_2 || '',
        city: followLead.lead_address?.city || '',
        county: followLead.lead_address?.county || '',
        postcode: followLead.lead_address?.postcode || '',
        // Source fields
        leadType: followLead.lead_source?.type || '',
        referral: followLead.lead_source?.referral || '',
        // Assignees
        bookedWith: followLead.assignees?.map((a) => ({
          id: a.id,
          first_name: a.first_name,
          last_name: a.last_name,
        })) || [],
        // Other fields
        preferredContactMethod: followLead.lead_contact?.preferred_contact_method || '',
        isRegister: followLead.is_registered_patient || false,
        // Identify caller fields (for IdentifyCallerLive component)
        callerFirstName: followLead.first_name || '',
        callerLastName: followLead.last_name || '',
        selectedLeadId: followLead.id || '',
        selectedLeadData: followLead,
      };

      // Merge initialData with prev, but initialData fills in empty/missing values
      setFormData((prev) => {
        const result = { ...prev };
        Object.entries(initialData).forEach(([key, value]) => {
          // Set from initialData if prev doesn't have a meaningful value
          if (result[key] === undefined || result[key] === null || result[key] === '') {
            result[key] = value;
          }
        });
        return result;
      });

      // Fetch treatment-specific scripts when followLead has a treatment (INBOUND scripts only)
      // Skip if user has manually changed treatment (handleSaveTreatment handles its own fetch)
      const treatmentName = getTreatmentName(activeCallScriptTreatment?.treatment);
      if (treatmentName && practiceId && scriptCategory === 'INBOUND' && !treatmentScriptsLoaded && !treatmentUserChangedRef.current) {
        requestedTreatmentRef.current = treatmentName;
        dispatch(getCallScriptSection({
          practiceId,
          treatment: treatmentName,
          isTemplate: false,
          category: 'INBOUND',
        }));
      }
    }
  }, [followLead, practiceId, scriptCategory, treatmentScriptsLoaded, activeCallScriptTreatment]);

  // Fetch email and WhatsApp templates for AI recommendations, and existing tags
  useEffect(() => {
    if (practiceId && !previewMode) {
      // Fetch email templates
      dispatch(getTemplateList(practiceId, true, null, true));
      // Fetch WhatsApp account (needed for WhatsApp templates)
      dispatch(getWhatsappAccount(practiceId));
      // Fetch existing tags for the practice (for tag matching on save)
      dispatch(getAllTagItem(practiceId, 100, 0, null, 'LEAD'));
    }
  }, [practiceId, previewMode]);

  // Fetch AI config on mount (needed for AI Call Summary feature toggle)
  // Always fetch to ensure we have the latest config from Practice Choices
  useEffect(() => {
    if (practiceId && !previewMode) {
      dispatch(fetchAiConfig(practiceId));
    }
  }, [practiceId, previewMode]);

  // Fetch WhatsApp templates once we have the account
  useEffect(() => {
    const connectedWhatsappAccount = getFirstConnectedWhatsappBusinessAccount(whatsappAccountData);
    if (connectedWhatsappAccount?.id && !previewMode) {
      dispatch(get10WpTemplateList({ wpAccountId: connectedWhatsappAccount.id }));
    }
  }, [whatsappAccountData, previewMode]);

  // Process treatment-specific scripts when callScriptData changes (after lead selection or treatment change)
  // For ALL categories: if a treatment-specific script exists, it REPLACES the base/master script entirely.
  // No fallback to default/base scripts - only use treatment-specific scripts.
  useEffect(() => {
    // Skip TCO scripts — they are provided via scriptData prop by CallScriptContainer.
    // Processing callScriptData for TCO causes a race condition: this effect clears Redux
    // callScriptData (via setCallScriptSection(null)) before the scriptData prop is available,
    // and the overrideBaseScript it sets may have empty cards if treatment matching fails.
    if (scriptCategory === 'TCO') {
      if (!treatmentScriptsLoaded) {
        setTreatmentScriptsLoaded(true);
      }
      return;
    }

    // Skip if treatment changed but script doesn't need to change (prevents unnecessary step reset/blink)
    if (skipScriptProcessingRef.current) {
      skipScriptProcessingRef.current = false;
      return;
    }

    // Skip if no callScriptData or already processed
    if (!callScriptData?.results || treatmentScriptsLoaded) {
      return;
    }

    // Get the current treatment from formData
    const currentTreatment = formData.treatment;
    if (!currentTreatment) {
      // Don't leave stale callScriptData in Redux when we can't process it here —
      // otherwise outbound's leftover payload survives across sessions and breaks
      // every subsequent inbound mount until a hard refresh.
      if (callScriptData?.results) {
        dispatch(setCallScriptSection(null));
      }
      return;
    }

    // Discard stale script responses from a previous treatment fetch (Priority 13 fix)
    if (requestedTreatmentRef.current && requestedTreatmentRef.current.toLowerCase().trim() !== currentTreatment.toLowerCase().trim()) {
      return;
    }

    // Filter out default scripts and ensure the script matches the current category
    // This prevents OUTBOUND scripts from being used in INBOUND mode and vice versa
    const nonDefaultScripts = callScriptData.results.filter((script) => {
      if (script.is_default) return false;
      // Ensure script category matches the current scriptCategory
      if (script.category && script.category !== scriptCategory) return false;
      return true;
    });

    // Verify and filter scripts that actually contain the current treatment in their treatments list
    // The API filters by treatment name, but we double-check here for correctness
    const treatmentLower = currentTreatment.toLowerCase().trim();
    const matchingScripts = nonDefaultScripts.filter((script) => {
      const scriptTreatments = script.treatments || [];
      return scriptTreatments.some((t) => {
        const itemName = (t.treatment_item?.name || t.name || '').toLowerCase().trim();
        return itemName === treatmentLower;
      });
    });

    if (matchingScripts.length === 0) {
      // No matching treatment-specific scripts found - keep current script, don't override
      // Mark as loaded so we don't keep retrying
      setTreatmentScriptsLoaded(true);
      dispatch(setCallScriptSection(null));
      return;
    }

    // Priority: prefer the script with the fewest treatments (most specific to this treatment)
    // If equal, prefer the most recently created one (first in the -created_at ordered results)
    const selectedScript = matchingScripts.length > 1
      ? matchingScripts.reduce((best, script) => {
        const bestCount = best.treatments?.length || Infinity;
        const scriptCount = script.treatments?.length || Infinity;
        return scriptCount < bestCount ? script : best;
      }, matchingScripts[0])
      : matchingScripts[0];

    // Check if script actually changed - only show modal if different
    const previousScriptId = previousScriptIdRef.current;
    const scriptActuallyChanged = previousScriptId !== null && previousScriptId !== selectedScript.id;

    // Show treatment switching modal only if script actually changed
    if (scriptActuallyChanged && currentTreatment) {
      setTreatmentSwitchingModal({
        open: true,
        message: `Switching to ${currentTreatment} script, populating saved data...`,
      });
      // Hide modal after 5 seconds
      setTimeout(() => {
        setTreatmentSwitchingModal({ open: false, message: '' });
      }, 5000);
    }

    // Clear the previous script ID ref after comparison
    previousScriptIdRef.current = null;

    setSelectedTemplateId(selectedScript.id);

    // Replace the entire base script with the treatment-specific script
    const scriptType = scriptCategory === 'OUTBOUND' ? 'outbound' : 'inbound';
    const transformedData = transformScriptData(selectedScript, scriptType);
    setOverrideBaseScript(transformedData);
    // Clear any additional cards (no longer using merge approach)
    setAdditionalScriptCards([]);
    setAdditionalScriptSections([]);
    setTreatmentScriptsLoaded(true);
    // Reset form submission state only if script actually changed
    if (scriptActuallyChanged) {
      formRef.current = { inProgress: false, formId: null, fieldMap: {}, fieldSubmissionMap: {}, pendingFormData: null };
    }

    // Mark cards as completed if their fields are already pre-filled (shows green dots)
    const newCards = transformedData.cards || [];
    const inputTypes = new Set(['text', 'textField', 'textarea', 'select', 'date', 'number', 'phone', 'email', 'choice', 'choiceGrid', 'scale', 'interest', 'lead_search_select', 'practice_treatment_search_select', 'practice_user_search_select']);
    const autoCompleted = new Set();
    const currentFormData = formDataRef.current;
    newCards.forEach((card, i) => {
      const inputElements = (card.elements || []).filter(el => inputTypes.has(el.type));
      if (inputElements.length === 0) {
        autoCompleted.add(i);
        return;
      }
      const allFilled = inputElements.every(el => {
        const val = currentFormData[el.field];
        return val !== undefined && val !== null && val !== '' &&
          !(Array.isArray(val) && val.length === 0);
      });
      if (allFilled) {
        autoCompleted.add(i);
      }
    });

    // Always start from step 1 - user can quickly Continue through pre-filled cards
    setCurrentCardIndex(0);
    setAutoFilledCards(autoCompleted);
    setCompletedCards(new Set());
    setSkippedCards(new Set());

    // Clear callScriptData in Redux to prevent re-processing
    dispatch(setCallScriptSection(null));
  }, [callScriptData, scriptCategory, formData.treatment, treatmentScriptsLoaded]);

  // Get cards and conditional logics from script data, including additional treatment-specific cards
  // For OUTBOUND: use overrideBaseScript when treatment changes replaces the entire script
  const effectiveScriptData = useMemo(() => overrideBaseScript || scriptData || {}, [overrideBaseScript, scriptData]);
  const baseCards = useMemo(() => effectiveScriptData.cards || [], [effectiveScriptData]);
  const allCards = useMemo(() => [...baseCards, ...additionalScriptCards], [baseCards, additionalScriptCards]);
  const baseScriptSections = useMemo(() => effectiveScriptData.sections || [], [effectiveScriptData]);
  const baseSections = useMemo(() => [...baseScriptSections, ...additionalScriptSections], [baseScriptSections, additionalScriptSections]);
  const conditionalLogics = useMemo(() => effectiveScriptData.conditionalLogics || [], [effectiveScriptData]);

  // Precompute which script ID would be selected for a given treatment name
  // Used to determine if changing treatment would actually change the script (avoid unnecessary loading)
  const getScriptIdForTreatment = useCallback((treatmentName) => {
    // Use appropriate data source based on category
    // INBOUND scripts are prefetched in inboundScriptsData, others in callScriptReusableData
    const scriptsSource = scriptCategory === 'INBOUND'
      ? inboundScriptsData?.results
      : callScriptReusableData?.results;

    if (!treatmentName || !scriptsSource) {
      return null;
    }

    const treatmentLower = treatmentName.toLowerCase().trim();

    // Filter scripts by treatment match (exclude default, template, and wrong category scripts)
    const matchingScripts = scriptsSource.filter((script) => {
      // Must not be template
      if (script.is_template) return false;
      // Must not be default script
      if (script.is_default) return false;
      // Must match the current script category (prevent OUTBOUND/INBOUND cross-matching)
      if (script.category && script.category !== scriptCategory) return false;
      // Must have the treatment in its treatments list
      const scriptTreatments = script.treatments || [];
      return scriptTreatments.some((t) => {
        const itemName = (t.treatment_item?.name || t.name || '').toLowerCase().trim();
        return itemName === treatmentLower;
      });
    });

    if (matchingScripts.length === 0) {
      // No matching treatment-specific script found - return null (no default/base script fallback)
      return null;
    }

    // Select the most specific script (fewest treatments)
    const selectedScript = matchingScripts.length > 1
      ? matchingScripts.reduce((best, script) => {
        const bestCount = best.treatments?.length || Infinity;
        const scriptCount = script.treatments?.length || Infinity;
        return scriptCount < bestCount ? script : best;
      }, matchingScripts[0])
      : matchingScripts[0];

    return selectedScript?.id || null;
  }, [inboundScriptsData?.results, callScriptReusableData?.results, scriptCategory]);

  // Get the treatments served by the currently loaded script
  // Used to check if a new treatment is already served by the current script (avoids unnecessary reload)
  // Uses effectiveScriptData.treatments which is preserved from the raw script data by transformScriptData
  const currentScriptTreatments = useMemo(() => {
    const treatments = effectiveScriptData?.treatments;
    if (!treatments || !Array.isArray(treatments) || treatments.length === 0) {
      return new Set();
    }
    // Build a Set of treatment names (lowercase, trimmed) for fast lookup
    return new Set(
      treatments.map((t) =>
        (t.treatment_item?.name || t.name || '').toLowerCase().trim()
      ).filter(Boolean)
    );
  }, [effectiveScriptData?.treatments]);

  // Build a mapping from field names to numeric IDs for API submissions
  // This maps element.field (e.g., 'firstName') to element.id (e.g., 123)
  // Supports both cards.elements (new format) and sections.fields (original API format)
  const fieldIdMap = useMemo(() => {
    const map = {};
    // Try cards.elements format first
    allCards.forEach((card) => {
      card.elements?.forEach((element) => {
        if (element.field && element.id) {
          map[element.field] = element.id;
        }
      });
    });
    // Also try sections.fields format (original API structure)
    baseSections.forEach((section) => {
      section.fields?.forEach((field) => {
        if (field.id) {
          // Map using field_${id} pattern (how original impl stores form data)
          map[`field_${field.id}`] = field.id;
          // Also map by field type name if available
          if (field.field_type?.name) {
            map[field.field_type.name] = field.id;
          }
        }
      });
    });
    return map;
  }, [allCards, baseSections]);

  // Build a reverse mapping from field IDs to field names (for conditional logic evaluation)
  const fieldIdToNameMap = useMemo(() => {
    const map = {};
    allCards.forEach((card) => {
      card.elements?.forEach((element) => {
        if (element.field && element.id) {
          map[element.id] = element.field;
        }
      });
    });
    return map;
  }, [allCards]);

  // Find the form-data key used by the treatment-picker field (typically 'treatment').
  // Look up via the practice_treatment_search_select element type rather than hardcoding,
  // so legacy forms with custom attribute strings still work.
  // Also collect the field IDs of every treatment-picker field AND every reason_for_calling
  // composite (which exposes treatmentInterest as a sub-field) — both feed condition matching.
  const treatmentMeta = useMemo(() => {
    let key = null;
    const ids = new Set();
    let hasReasonForCalling = false;
    allCards.forEach((card) => {
      card.elements?.forEach((el) => {
        if (el?.type === 'practice_treatment_search_select') {
          if (!key && el.field) key = el.field;
          if (el.id != null) ids.add(el.id);
        }
        // The reason_for_calling special widget composes a treatmentInterest sub-field,
        // and many scripts target that sub-field via conditions with isSubField=true.
        if (el?.type === 'reason_for_calling') {
          hasReasonForCalling = true;
          if (el.id != null) ids.add(el.id);
        }
      });
    });
    return { key, ids, hasReasonForCalling };
  }, [allCards]);

  // Collect every sub-field name used by conditions whose fieldId targets a treatment-related
  // field. In production scripts the most common sub-field is 'treatmentInterest', surfaced by
  // the reason_for_calling component — we need to seed it explicitly because evaluator looks
  // up sub-fields by name (not by numeric id mapping). See evaluateMetadataCondition L196.
  // We also pick up sub-field-keyed conditions whose fieldId doesn't match a known field at all
  // (legacy/external IDs) — for those we trust the subFieldName and seed it anyway.
  const treatmentSubFieldNames = useMemo(() => {
    const names = new Set();
    const harvest = (logic) => {
      logic?.conditions?.forEach((cond) => {
        if (cond?.isSubField && cond?.subFieldName === 'treatmentInterest') {
          names.add(cond.subFieldName);
        } else if (treatmentMeta.ids.has(cond?.fieldId) && cond?.isSubField && cond?.subFieldName) {
          names.add(cond.subFieldName);
        }
      });
    };
    allCards.forEach((card) => {
      harvest(card?.metadata?.conditionLogic);
    });
    baseSections.forEach((section) => {
      harvest(section?.conditionLogic);
    });
    return names;
  }, [allCards, baseSections, treatmentMeta.ids]);

  // True when Preview is active AND a treatment has been picked to simulate.
  // We engage when there's any way to seed treatment data: either a real treatment key
  // (practice_treatment_search_select) or a reason_for_calling composite that exposes
  // treatmentInterest as a sub-field (the common case in production scripts).
  const canSimulate = !!treatmentMeta.key || treatmentMeta.hasReasonForCalling || treatmentSubFieldNames.size > 0;
  const isSimulating = previewMode && !!simulatedTreatment?.name && canSimulate;

  // effectiveFormData is for READ-ONLY use by the conditional-logic evaluator during
  // simulation. It does NOT include real lead/answer fields when isSimulating is true —
  // only the synthetic treatment seed. NEVER pass this to a save/submit call; use
  // `formData` for any write path.
  //
  // When simulating, build a synthetic formData containing the treatment seed.
  // Per spec: fill BOTH treatment name AND category so `contains` conditions on either match.
  // Additionally seed every sub-field name used by treatment conditions (e.g. 'treatmentInterest')
  // so 'isSubField: true' conditions match. We also seed the literal 'treatment' / 'treatmentInterest'
  // as safety nets since they are by far the most common keys in production scripts.
  const effectiveFormData = useMemo(() => {
    if (!isSimulating) return formData;
    const seedArray = [simulatedTreatment.name];
    if (simulatedTreatment.category) {
      seedArray.push(simulatedTreatment.category);
    }
    const seed = {};
    if (treatmentMeta.key) {
      seed[treatmentMeta.key] = seedArray;
    }
    // Sub-fields are scalar in formData (a single name string), per evaluator handling.
    treatmentSubFieldNames.forEach((subName) => {
      seed[subName] = simulatedTreatment.name;
    });
    // Belt-and-braces seeds for common conventions across legacy/new scripts.
    if (!('treatmentInterest' in seed)) {
      seed.treatmentInterest = simulatedTreatment.name;
    }
    if (!('treatment' in seed)) {
      seed.treatment = seedArray;
    }
    return seed;
  }, [isSimulating, simulatedTreatment, treatmentMeta.key, treatmentSubFieldNames, formData]);

  // Check if close_call attribute exists in the script
  // If it does, we hide the default closure section
  const hasCloseCallAttribute = useMemo(() => allCards.some((card) =>
    card.elements?.some((element) => element.type === 'close_call')
  ), [allCards]);

  // Calculate data completeness percentage
  const dataCompleteness = useMemo(() => {
    let totalFields = 0;
    let filledFields = 0;

    allCards.forEach((card) => {
      card.elements?.forEach((element) => {
        // Count input-type elements (not say blocks, tips, etc.)
        if (element.type && ['text', 'textarea', 'select', 'date', 'number', 'phone', 'email', 'choice', 'scale', 'interest'].includes(element.type)) {
          totalFields += 1;
          const fieldValue = formData[element.field];
          // Check if field has a value
          if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '' &&
              !(Array.isArray(fieldValue) && fieldValue.length === 0)) {
            filledFields += 1;
          }
        }
      });
    });

    return {
      total: totalFields,
      filled: filledFields,
      percentage: totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0,
    };
  }, [allCards, formData]);

  // Check if any treatment is available for the current session
  // Used for default section filtering - if no treatment, only show default sections
  const hasTreatmentAvailable = useMemo(() => {
    // In preview mode, show all sections (assume treatment is available)
    if (previewMode) return true;

    // Check formData.treatment (current selection from lead or user input)
    if (formData.treatment && formData.treatment.trim() !== '') {
      return true;
    }

    // Check if followLead has any treatments
    if (followLead?.lead_treatments?.length > 0) {
      return true;
    }

    return false;
  }, [previewMode, formData.treatment, followLead?.lead_treatments]);

  // Get default section IDs from sections with isDefaultSection flag
  // (set by scriptDataTransformer when section name ends with $default)
  const defaultSectionIds = useMemo(() => {
    const ids = new Set();
    baseSections.forEach((section) => {
      if (section.isDefaultSection) {
        ids.add(section.originalId);
      }
    });
    return ids;
  }, [baseSections]);

  // Without a treatment only the "$default" sections show. A script with no "$default"
  // section at all would then show nothing ("0 of 0", "NaN%"): every section stays
  // visible instead (CU-869fmcc9r; e.g. a copied inbound script for an unknown caller).
  const hideNonDefaultSections = !isSimulating && !hasTreatmentAvailable && defaultSectionIds.size > 0;

  // Track which sections are hidden based on default section logic and conditional logic
  const hiddenSectionIds = useMemo(() => {
    // Bypass everything in plain preview mode; engage real evaluator when simulating.
    if (previewMode && !isSimulating) return new Set();

    const hidden = new Set();
    baseSections.forEach((section) => {
      // First check: If no treatment available, hide non-default sections
      // Match against originalId since that's the API section ID used in conditional_logics.
      // When simulating, the synthetic formData has a treatment, so this branch is skipped.
      const isDefault = defaultSectionIds.has(section.originalId);
      if (hideNonDefaultSections && !isDefault) {
        hidden.add(section.id);
        return; // Skip further checks for already hidden sections
      }

      // Second check: Evaluate conditional logic against effective formData
      // (synthetic when simulating, real otherwise).
      if (section.conditionLogic) {
        const isVisible = evaluateMetadataConditionLogic(
          section.conditionLogic,
          effectiveFormData,
          fieldIdToNameMap
        );
        if (!isVisible) {
          hidden.add(section.id);
        }
      }
    });
    return hidden;
  }, [baseSections, effectiveFormData, fieldIdToNameMap, previewMode, isSimulating, hideNonDefaultSections, defaultSectionIds]);

  // Calculate which cards are visible based on conditional logic
  // Supports: section-level conditional logic, field-level conditionalLogics array, AND field metadata.conditionLogic
  const cards = useMemo(() => allCards.filter((card) => {
      // In plain preview mode (no simulation): show every card so editors see the full script.
      // In simulation mode: run the real evaluator against synthetic formData.
      if (previewMode && !isSimulating) {
        return true;
      }

      // 0. Check if card's section is hidden by section-level conditional logic
      if (hiddenSectionIds.has(card.section)) {
        return false; // Hidden by section conditional logic
      }

      // 1. Check existing conditionalLogics array (legacy system)
      if (conditionalLogics && conditionalLogics.length > 0) {
        const logic = conditionalLogics.find(
          (cl) => cl.target_section === card.originalSectionId && cl.target_field === card.fieldId
        );

        if (logic) {
          const isVisibleByLegacy = evaluateConditionalLogic(logic, effectiveFormData, fieldIdToNameMap);
          if (!isVisibleByLegacy) {
            return false; // Hidden by legacy conditional logic
          }
        }
      }

      // 2. Check metadata.conditionLogic (new feature)
      if (card.metadata?.conditionLogic) {
        const isVisibleByMetadata = evaluateMetadataConditionLogic(
          card.metadata.conditionLogic,
          effectiveFormData,
          fieldIdToNameMap
        );
        if (!isVisibleByMetadata) {
          return false; // Hidden by metadata conditional logic
        }
      }

      // Card is visible (passed all conditional checks or has no conditions)
      return true;
    }), [allCards, conditionalLogics, effectiveFormData, fieldIdToNameMap, previewMode, isSimulating, hiddenSectionIds]);

  // Add closure section to sections with numeric ID (max section ID + 1)
  const closureSectionId = useMemo(() => {
    const maxId = baseSections.reduce((max, s) => Math.max(max, s.id || 0), 0);
    return maxId + 1;
  }, [baseSections]);

  // Filter sections based on default section logic and conditional logic
  const visibleBaseSections = useMemo(() => {
    // Plain preview (no simulation): show every section.
    if (previewMode && !isSimulating) {
      return baseSections;
    }

    return baseSections.filter((section) => {
      // First check: If no treatment available, only show default sections.
      // When simulating, the synthetic formData has a treatment, so we skip this branch.
      if (hideNonDefaultSections && !defaultSectionIds.has(section.originalId)) {
        return false;
      }

      // Second check: If section has no conditionLogic, it's always visible
      if (!section.conditionLogic) {
        return true;
      }

      // Third check: Evaluate section's conditional logic against effective formData
      return evaluateMetadataConditionLogic(
        section.conditionLogic,
        effectiveFormData,
        fieldIdToNameMap
      );
    });
  }, [baseSections, effectiveFormData, fieldIdToNameMap, previewMode, isSimulating, hideNonDefaultSections, defaultSectionIds]);

  const sections = useMemo(() => {
    // Only add the Call Outcome section if close_call attribute is NOT present in the script
    if (hasCloseCallAttribute) {
      return visibleBaseSections;
    }
    return [
      ...visibleBaseSections,
      { id: closureSectionId, name: 'Call Outcome' },
    ];
  }, [visibleBaseSections, closureSectionId, hasCloseCallAttribute]);

  // Track if a treatment-specific script is being loaded (for loading UX)
  const isScriptSwitching = treatmentUserChangedRef.current && !treatmentScriptsLoaded && !!formData.treatment;

  // Track if we're on the closure section (after all cards)
  // If close_call attribute is present, we never go to a separate closure section
  const isOnClosureSection = !hasCloseCallAttribute && currentCardIndex >= cards.length;

  // Current card data
  const currentCard = isOnClosureSection ? null : cards[currentCardIndex];
  const currentSectionId = isOnClosureSection ? closureSectionId : (currentCard?.section || 1);

  // Calculate progress (including closure as +1 only if close_call attribute is NOT present)
  const totalSteps = hasCloseCallAttribute ? cards.length : cards.length + 1;
  const currentStep = Math.min(currentCardIndex + 1, totalSteps);

  // Section progress calculations
  const completedSections = useMemo(() => {
    const completed = [];
    sections.forEach((section) => {
      const sectionCards = cards.filter((c) => c.section === section.id);
      const sectionIndices = sectionCards.map((c) => cards.indexOf(c));
      const allDone = sectionIndices.every(
        (idx) => completedCards.has(idx) || skippedCards.has(idx)
      );
      const lastCardIndex = Math.max(...sectionIndices);
      if (allDone && currentCardIndex > lastCardIndex) {
        completed.push(section.id);
      }
    });
    return completed;
  }, [sections, cards, completedCards, skippedCards, currentCardIndex]);

  const sectionsWithSkipped = useMemo(() => {
    const withSkipped = [];
    sections.forEach((section) => {
      const sectionCards = cards.filter((c) => c.section === section.id);
      const sectionIndices = sectionCards.map((c) => cards.indexOf(c));
      if (sectionIndices.some((idx) => skippedCards.has(idx))) {
        withSkipped.push(section.id);
      }
    });
    return withSkipped;
  }, [sections, cards, skippedCards]);

  // Questions for current section
  const currentSectionQuestions = useMemo(() => cards.filter((c) => c.section === currentSectionId), [cards, currentSectionId]);

  // Pre-compute global index for each card in the section (avoids O(n) indexOf per card during render)
  const sectionCardGlobalIndices = useMemo(() => {
    const indexMap = new Map();
    cards.forEach((c, i) => { indexMap.set(c, i); });
    return currentSectionQuestions.map((c) => indexMap.get(c) ?? -1);
  }, [cards, currentSectionQuestions]);

  const currentQuestionInSection = useMemo(() => currentSectionQuestions.findIndex(
    (c, idx) => sectionCardGlobalIndices[idx] === currentCardIndex
  ), [currentSectionQuestions, sectionCardGlobalIndices, currentCardIndex]);

  // Notify parent of card changes (for import modal JSON highlighting)
  useEffect(() => {
    if (onCardChange && currentCard) {
      onCardChange({
        cardIndex: currentCardIndex,
        sectionIndex: currentCard.section - 1,
        questionIndex: currentCard.question - 1,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCardIndex]);

  // Handle external navigation request (from import modal toolbar)
  useEffect(() => {
    if (navigateTo && typeof navigateTo.sectionIndex === 'number' && typeof navigateTo.questionIndex === 'number') {
      // Find the card matching the section and question (0-indexed input, cards use 1-indexed)
      const targetCardIndex = cards.findIndex(
        (card) => card.section - 1 === navigateTo.sectionIndex && card.question - 1 === navigateTo.questionIndex
      );
      if (targetCardIndex !== -1 && targetCardIndex !== currentCardIndex) {
        setCurrentCardIndex(targetCardIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigateTo, cards]);

  // Progress data for context panel
  const progressData = useMemo(() => ({
    current: currentStep,
    total: totalSteps,
    sections: sections.map((s) => {
      // Handle closure section specially
      if (s.id === closureSectionId) {
        return {
          id: s.id,
          name: s.name,
          isActive: isOnClosureSection,
          isCompleted: !!formData.callOutcome,
          isSkipped: false,
        };
      }

      const sectionCards = cards.filter((c) => c.section === s.id);
      const sectionIndices = sectionCards.map((c) => cards.indexOf(c));
      const isActive = currentSectionId === s.id;
      const isCompleted = completedSections.includes(s.id);
      const isSkipped = sectionIndices.some((idx) => skippedCards.has(idx));

      return {
        id: s.id,
        name: s.name,
        isActive,
        isCompleted,
        isSkipped,
      };
    }),
  }), [currentStep, totalSteps, sections, cards, currentSectionId, completedSections, skippedCards, isOnClosureSection, formData.callOutcome, closureSectionId]);

  // Collected data for context panel
  const collectedData = useMemo(() => {
    const data = [];
    if (formData.firstName) {
      data.push({ label: 'Name', value: `${formData.title || ''} ${formData.firstName} ${formData.lastName || ''}`.trim() });
    }
    if (formData.phone || formData.phone_number) {
      data.push({ label: 'Phone', value: formData.phone || formData.phone_number });
    }
    if (formData.email) {
      data.push({ label: 'Email', value: formData.email });
    }
    if (formData.treatment) {
      data.push({ label: 'Treatment', value: formData.treatment });
    }
    if (formData.source?.length) {
      data.push({ label: 'Source', value: Array.isArray(formData.source) ? formData.source.join(', ') : formData.source });
    }
    if (formData.leadType) {
      data.push({ label: 'Lead Type', value: formData.leadType });
    }
    return data;
  }, [formData]);

  // Patient data for context panel
  // Priority: formData (user-edited) > followLead (from Redux) > leadData (prop) > fallback
  // In preview mode, show placeholder data
  const patientData = useMemo(() => {
    if (previewMode) {
      return {
        name: 'Preview Mode',
        initials: 'PM',
        status: 'Script Preview · No lead selected',
      };
    }

    return {
      name: (() => {
        if (formData.firstName) {
          return `${formData.title || ''} ${formData.firstName} ${formData.lastName || ''}`.trim();
        }
        if (followLead?.first_name) {
          return safeJoinName(followLead.first_name, followLead.last_name);
        }
        if (leadData.name && leadData.name !== 'Unknown Caller') {
          return leadData.name;
        }
        return 'Unknown Caller';
      })(),
      initials: (() => {
        if (formData.firstName) {
          return `${formData.firstName[0]}${formData.lastName?.[0] || ''}`.toUpperCase();
        }
        if (followLead?.first_name) {
          return `${followLead.first_name[0]}${followLead.last_name?.[0] || ''}`.toUpperCase();
        }
        if (leadData.initials && leadData.initials !== '?') {
          return leadData.initials;
        }
        return '?';
      })(),
      status: (() => {
        if (scriptCategory === 'TCO') return 'TCO · Capturing details';
        if (type === 'inbound') return 'Inbound call · Capturing details';
        return 'Outbound · Following up on enquiry';
      })(),
    };
  }, [formData, leadData, followLead, type, scriptCategory, previewMode]);

  // Helper to set form ref state
  const setFormState = useCallback((newState) => {
    formRef.current = { ...formRef.current, ...newState };
  }, []);

  // Auto-save lead details and script submission using Redux actions
  const autoSaveLeadDetails = useCallback((updatedData) => {
    // Skip auto-save in preview mode
    if (previewMode) {
      setSaveStatus('idle');
      return;
    }

    const leadId = updatedData.lead_id || formData.lead_id;
    const scriptId = effectiveScriptData?.id;

    // Don't auto-save if no lead is selected or no practiceId
    if (!leadId || !practiceId) {
      setSaveStatus(!leadId ? 'waiting_for_lead' : 'idle');
      return;
    }

    setSaveStatus('saving');

    // 1. Save lead details via updateAssignToUser
    const leadPayload = {
      id: leadId, // Include lead ID in request body for PATCH
      first_name: updatedData.firstName ?? formData.firstName,
      last_name: updatedData.lastName ?? formData.lastName,
      salutation: updatedData.title ?? formData.title,
      dob: updatedData.date_of_birth ?? formData.date_of_birth,
      lead_contact: {
        phone: updatedData.phone || updatedData.phone_number || formData.phone || formData.phone_number,
        email: updatedData.email ?? formData.email,
        preferred_contact_method: updatedData.preferredContactMethod ?? formData.preferredContactMethod,
      },
      // Address is a separate object from lead_contact
      lead_address: {
        address_line_1: updatedData.address_line_1 ?? formData.address_line_1,
        address_line_2: updatedData.address_line_2 ?? formData.address_line_2,
        city: updatedData.city ?? formData.city,
        county: updatedData.county ?? formData.county,
        postcode: updatedData.postcode ?? formData.postcode,
      },
      lead_source: {
        type: updatedData.leadType ?? formData.leadType,
        referral: updatedData.referral ?? formData.referral,
      },
    };

    // Filter out undefined values from payload
    Object.keys(leadPayload).forEach(key => {
      if (leadPayload[key] === undefined) {
        delete leadPayload[key];
      }
    });

    // Filter out undefined and empty string values from lead_contact
    // Choice fields (preferred_contact_method) reject empty strings as invalid choices
    if (leadPayload.lead_contact) {
      Object.keys(leadPayload.lead_contact).forEach(key => {
        const value = leadPayload.lead_contact[key];
        // Remove undefined values
        if (value === undefined) {
          delete leadPayload.lead_contact[key];
        }
        // Remove empty strings for choice fields (they're not valid choices)
        if (key === 'preferred_contact_method' && value === '') {
          delete leadPayload.lead_contact[key];
        }
      });
      if (Object.keys(leadPayload.lead_contact).length === 0) {
        delete leadPayload.lead_contact;
      }
    }

    // Filter out undefined values from lead_address
    if (leadPayload.lead_address) {
      Object.keys(leadPayload.lead_address).forEach(key => {
        const value = leadPayload.lead_address[key];
        if (value === undefined) {
          delete leadPayload.lead_address[key];
        }
      });
      if (Object.keys(leadPayload.lead_address).length === 0) {
        delete leadPayload.lead_address;
      }
    }

    // Filter out undefined and empty string values from lead_source
    // Choice fields (type, referral) reject empty strings as invalid choices
    if (leadPayload.lead_source) {
      Object.keys(leadPayload.lead_source).forEach(key => {
        const value = leadPayload.lead_source[key];
        // Remove undefined values
        if (value === undefined) {
          delete leadPayload.lead_source[key];
        }
        // Remove empty strings for choice fields (they're not valid choices)
        if ((key === 'type' || key === 'referral') && value === '') {
          delete leadPayload.lead_source[key];
        }
      });
      if (Object.keys(leadPayload.lead_source).length === 0) {
        delete leadPayload.lead_source;
      }
    }

    // Update lead details
    dispatch(updateAssignToUser(
      leadPayload,
      leadId,
      practiceId,
      () => {}, // Silent callback for lead update
      null,
      'leadUpdate'
    ));

    // 2. Save script form submission via createSubmission/updateSubmission
    if (scriptId) {
      // Build submission data from form fields
      const allFormData = { ...formData, ...updatedData };

      // Build composite value for captureContactDetails if the field exists in fieldIdMap
      if (fieldIdMap.captureContactDetails !== undefined) {
        const contactDetailsValue = JSON.stringify({
          phone_number: allFormData.phone_number || allFormData.phone || '',
          email: allFormData.email || '',
          date_of_birth: allFormData.date_of_birth || '',
          address_line_1: allFormData.address_line_1 || '',
          address_line_2: allFormData.address_line_2 || '',
          city: allFormData.city || '',
          county: allFormData.county || '',
          postcode: allFormData.postcode || '',
          referral: allFormData.referral || '',
        });
        // Only include if at least one sub-field has a value
        const hasAnyContactValue = allFormData.phone_number || allFormData.phone || allFormData.email ||
          allFormData.address_line_1 || allFormData.referral || allFormData.date_of_birth;
        if (hasAnyContactValue) {
          allFormData.captureContactDetails = contactDetailsValue;
        }
      }

      // Build composite value for closeCall if the field exists in fieldIdMap
      if (fieldIdMap.closeCall !== undefined) {
        const closeCallValue = JSON.stringify({
          interestLevel: allFormData.interestLevel || '',
          callOutcome: allFormData.callOutcome || '',
        });
        // Only include if at least one sub-field has a value
        const hasAnyCloseCallValue = allFormData.interestLevel || allFormData.callOutcome;
        if (hasAnyCloseCallValue) {
          allFormData.closeCall = closeCallValue;
        }
      }

      // Map form data to submission fields format
      // Only include fields that have a numeric ID in fieldIdMap (script fields)
      const submissionFields = Object.entries(allFormData)
        .filter(([key]) => key)
        .filter(([key, value]) => {
          // Must have a value and must be a valid script field with numeric ID
          const hasValue = value !== undefined && value !== '' && value !== null;
          const hasFieldId = fieldIdMap[key] !== undefined;
          return hasValue && hasFieldId;
        })
        .map(([key, value]) => {
          // Handle different value types
          let fieldValue;
          if (Array.isArray(value)) {
            // Check if it's an array of objects (like users/assignees)
            if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
              // Extract IDs from objects (for bookedWith/assignees)
              fieldValue = value.map((item) => item.id).join(' | ');
            } else {
              // Regular array of primitives
              fieldValue = value.join(' | ');
            }
          } else {
            fieldValue = String(value);
          }
          // Use numeric field ID from fieldIdMap, not the field name
          const numericFieldId = fieldIdMap[key];
          const fieldData = { field: numericFieldId, value: fieldValue };
          // Add submission field ID if we have it from previous submission (for updates)
          // Use String() for consistent key lookup and explicit undefined check
          const submissionEntryId = formRef.current?.fieldMap?.[String(numericFieldId)];
          if (submissionEntryId !== undefined && submissionEntryId !== null) {
            fieldData.id = submissionEntryId;
          }
          return fieldData;
        });

      // Only make API call if we have valid fields to submit
      if (submissionFields.length === 0) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 3000);
        return;
      }

      const submissionData = {
        practice: Number(practiceId),
        lead: leadId,
        formId: scriptId,
        fields: submissionFields,
      };

      // Helper to process pending submission after current one completes
      const processPendingSubmission = () => {
        if (formRef.current?.pendingFormData) {
          const pendingData = formRef.current.pendingFormData;
          setFormState({ pendingFormData: null });
          // Re-trigger save with pending data after a short delay to allow state to settle
          setTimeout(() => {
            autoSaveLeadDetails(pendingData);
          }, 100);
        }
      };

      // If a submission is already in progress, queue this data for later
      if (formRef.current?.inProgress) {
        // Store pending data - will be submitted after current one completes
        setFormState({ pendingFormData: allFormData });
        setSaveStatus('saving');
        return;
      }

      // Create or update submission
      if (!formRef.current?.formId) {
        // First time - create submission
        setFormState({ inProgress: true });
        dispatch(createSubmission(submissionData, (result) => {
          if (result?.success) {
            // Store the submission ID and field map for future updates
            // Use String(field.field) as key for consistent lookup
            const fieldMap = {};
            result.data?.fields?.forEach(field => {
              if (field?.field !== undefined && field?.id !== undefined) {
                fieldMap[String(field.field)] = field.id;
              }
            });
            setFormState({
              formId: result.data?.id,
              inProgress: false,
              fieldMap
            });
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 3000);
            // Process any pending submission
            processPendingSubmission();
          } else {
            setFormState({ inProgress: false });
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 3000);
            // Still try to process pending on error (might succeed with updated fieldMap)
            processPendingSubmission();
          }
        }, 'autosave'));
      } else {
        // Update existing submission - include submission ID in the data
        setFormState({ inProgress: true });
        const updateData = {
          ...submissionData,
          id: formRef.current.formId, // Include submission ID in the request body
        };
        dispatch(updateSubmission(
          practiceId,
          scriptId,
          formRef.current.formId,
          updateData,
          (result) => {
            if (result?.success) {
              // Update fieldMap with field submission IDs from response (for new fields added)
              // Use String(field.field) as key for consistent lookup
              const updatedFieldMap = { ...formRef.current.fieldMap };
              result.data?.fields?.forEach(field => {
                if (field?.field !== undefined && field?.id !== undefined) {
                  updatedFieldMap[String(field.field)] = field.id;
                }
              });
              setFormState({ inProgress: false, fieldMap: updatedFieldMap });
              setSaveStatus('saved');
              setTimeout(() => setSaveStatus('idle'), 3000);
              // Process any pending submission
              processPendingSubmission();
            } else {
              setFormState({ inProgress: false });
              setSaveStatus('error');
              setTimeout(() => setSaveStatus('idle'), 3000);
              // Still try to process pending on error
              processPendingSubmission();
            }
          },
          'autosave'
        ));
      }
    } else {
      // No script ID, just show saved for lead update
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }

    // 3. Save call notes via addNotes (POST) or updateNotes (PATCH) API
    const currentNotes = updatedData.callNotes || callNotes;
    if (currentNotes && currentNotes.trim() && currentNotes.trim() !== notesRef.current.lastSavedContent) {
      const treatmentId = updatedData.treatment_id || formData.treatment_id || activeCallScriptTreatment?.id;
      const notesData = {
        lead: leadId,
        additional_notes: currentNotes.trim(),
        note: 'FOLLOW_UP',
      };
      if (treatmentId) {
        notesData.treatment = treatmentId;
      }

      // Use PATCH if we have an existing note ID, otherwise POST to create new note
      if (notesRef.current.noteId) {
        const updateNotesData = {
          ...notesData,
          id: notesRef.current.noteId, // Include note ID in the request body
        };
        dispatch(updateNotes(updateNotesData, practiceId, notesRef.current.noteId, (result) => {
          if (result?.success) {
            notesRef.current.lastSavedContent = currentNotes.trim();
          }
        }, 'callScript'));
      } else {
        dispatch(addNotes(notesData, practiceId, (result) => {
          if (result?.success || result?.status === 201) {
            notesRef.current.lastSavedContent = currentNotes.trim();
            notesRef.current.noteId = result?.data?.id || result?.id;
          }
        }, 'callScript'));
      }
    }
  }, [formData, practiceId, effectiveScriptData?.id, setFormState, fieldIdMap, callNotes, followLead?.lead_treatments, previewMode, activeCallScriptTreatment]);

  // Debounced auto-save
  const debouncedAutoSave = useCallback((updatedFormData) => {
    // Clear any existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Store pending changes
    pendingChangesRef.current = { ...pendingChangesRef.current, ...updatedFormData };

    // Set new timeout for auto-save (1.5 seconds debounce)
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveLeadDetails(pendingChangesRef.current);
      pendingChangesRef.current = {};
      // Also save to IndexedDB draft (persists even if API save is skipped due to missing lead_id)
      saveDraftToIndexedDB();
    }, 1500);
  }, [autoSaveLeadDetails, saveDraftToIndexedDB]);

  // Handlers
  const handleFormChange = useCallback((field, value) => {
    const updatedData = { [field]: value };
    setFormData((prev) => ({ ...prev, ...updatedData }));

    // Clear validation errors when user starts filling fields
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }

    // Auto-save all form changes (like original implementation)
    // Only if a lead is selected
    if (formData.lead_id || followLead?.id) {
      setSaveStatus('saving');
      debouncedAutoSave({ ...updatedData, lead_id: formData.lead_id || followLead?.id });
    } else {
      // No lead yet — still save to local draft so data isn't lost
      saveDraftToIndexedDB();
    }
  }, [validationErrors.length, formData.lead_id, followLead?.id, debouncedAutoSave, saveDraftToIndexedDB]);

  // Cleanup timeouts on unmount — flush any pending changes first (Priority 3 fix)
  useEffect(() => () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    if (treatmentSaveTimeoutRef.current) {
      clearTimeout(treatmentSaveTimeoutRef.current);
    }
    // Flush any pending changes before unmount (fire-and-forget — API call survives unmount)
    if (pendingChangesRef.current && Object.keys(pendingChangesRef.current).length > 0) {
      const leadId = formDataRef.current.lead_id;
      if (leadId) {
        autoSaveLeadDetails({ ...pendingChangesRef.current, lead_id: leadId });
      }
      pendingChangesRef.current = {};
    }
  }, []);

  // Validate required fields across all cards
  const validateRequiredFields = useCallback(() => {
    const errors = [];

    cards.forEach((card, cardIndex) => {
      card.elements?.forEach((element) => {
        if (element.required) {
          // Multi-field special components store data under different keys than element.field
          let isEmpty;
          if (element.type === 'capture_contact_details') {
            isEmpty = !(formData.phone_number || formData.email);
          } else if (element.type === 'close_call') {
            isEmpty = !formData.callOutcome;
          } else if (element.type === 'identify_caller') {
            isEmpty = !formData.callerType;
          } else {
            const fieldName = element.field;
            const value = formData[fieldName];
            isEmpty = value === undefined ||
              value === null ||
              value === '' ||
              (Array.isArray(value) && value.length === 0);
          }

          if (isEmpty) {
            errors.push({
              cardIndex,
              fieldName: element.field,
              label: element.label || element.field,
              cardTitle: card.title,
              sectionId: card.section,
            });
          }
        }
      });
    });

    return errors;
  }, [cards, formData]);

  // Navigate to a specific card with error
  const navigateToError = useCallback((error) => {
    setCurrentCardIndex(error.cardIndex);
    setValidationErrors([]);
  }, []);

  // Helper to trigger auto-create for IdentifyCaller if user has unsaved changes
  const triggerIdentifyCallerAutoCreate = useCallback(() => {
    if (identifyCallerRef.current?.autoCreateLeadIfNeeded) {
      const firstName = (formDataRef.current.callerFirstName || '').trim();
      const lastName = (formDataRef.current.callerLastName || '').trim();
      const callerName = safeJoinName(firstName, lastName) || 'Unknown';
      const didTrigger = identifyCallerRef.current.autoCreateLeadIfNeeded();
      if (didTrigger) {
        enqueueSnackbar(`Auto-saving lead as "${callerName}"`, { variant: 'info', autoHideDuration: 5000 });
      }
    }
  }, [enqueueSnackbar]);

  const handleNext = useCallback(() => {
    // If on closure section, don't proceed (use outcome buttons instead)
    if (isOnClosureSection) {
      return;
    }

    // Auto-create lead if IdentifyCaller has unsaved changes
    triggerIdentifyCallerAutoCreate();

    // Check if current card has required fields that are not filled
    const currentCard = cards[currentCardIndex];
    const hasUnfilledRequired = currentCard?.elements?.some((element) => {
      if (!element.required) return false;

      // Multi-field special components store data under different keys than element.field
      if (element.type === 'capture_contact_details') {
        return !(formData.phone_number || formData.email);
      }
      if (element.type === 'close_call') {
        return !formData.callOutcome;
      }
      if (element.type === 'identify_caller') {
        return !formData.callerType;
      }

      const fieldName = element.field;
      const value = formData[fieldName];
      return value === undefined || value === null || value === '' ||
        (Array.isArray(value) && value.length === 0);
    });

    if (hasUnfilledRequired) {
      // Mark as skipped if mandatory fields not filled
      setSkippedCards((prev) => new Set([...prev, currentCardIndex]));
      // Remove from completed if it was completed
      setCompletedCards((prev) => {
        const newSet = new Set(prev);
        newSet.delete(currentCardIndex);
        return newSet;
      });
    } else {
      // Mark current card as completed
      setCompletedCards((prev) => new Set([...prev, currentCardIndex]));
      // Remove from skipped if it was skipped
      setSkippedCards((prev) => {
        const newSet = new Set(prev);
        newSet.delete(currentCardIndex);
        return newSet;
      });
    }

    if (scrollableView) {
      // In scrollable mode, check ALL cards in the current section for completion
      // Use currentSectionQuestions (memoized) instead of re-filtering
      const currentSectionCards = currentSectionQuestions;
      // Build index map once to avoid O(n) indexOf per card
      const cardIndexMap = new Map();
      cards.forEach((c, i) => { if (c.section === currentSectionId) cardIndexMap.set(c, i); });
      const sectionGlobalIndices = currentSectionCards.map((c) => cardIndexMap.get(c) ?? cards.indexOf(c));

      // Helper: check if a card has unfilled required fields
      const cardHasUnfilledRequired = (card) => card?.elements?.some((element) => {
        if (!element.required) return false;
        if (element.type === 'capture_contact_details') return !(formData.phone_number || formData.email);
        if (element.type === 'close_call') return !formData.callOutcome;
        if (element.type === 'identify_caller') return !formData.callerType;
        const value = formData[element.field];
        return value === undefined || value === null || value === '' ||
          (Array.isArray(value) && value.length === 0);
      });

      // Helper: check if a card has been interacted with (any field filled)
      const cardIsInteracted = (card, globalIdx) => {
        if (completedCards.has(globalIdx) || skippedCards.has(globalIdx)) return true;
        return card?.elements?.some((element) => {
          if (!element.field) return false;
          if (element.type === 'capture_contact_details') return !!(formData.phone_number || formData.email);
          if (element.type === 'close_call') return !!formData.callOutcome;
          if (element.type === 'identify_caller') return !!formData.callerType;
          const value = formData[element.field];
          return value !== undefined && value !== null && value !== '' &&
            !(Array.isArray(value) && value.length === 0);
        });
      };

      // Find first card in section with unfilled mandatory fields
      const firstMandatoryUnfilled = currentSectionCards.findIndex((card, idx) => {
        const globalIdx = sectionGlobalIndices[idx];
        return globalIdx !== currentCardIndex && cardHasUnfilledRequired(card);
      });

      if (firstMandatoryUnfilled !== -1) {
        // There's an unfilled mandatory card — scroll to it
        const targetGlobalIdx = sectionGlobalIndices[firstMandatoryUnfilled];
        setCurrentCardIndex(targetGlobalIdx);
        const cardRef = scrollableCardRefs.current[targetGlobalIdx];
        if (cardRef) {
          setTimeout(() => cardRef.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
        }
      } else {
        // All mandatory fields are filled — mark non-interacted non-mandatory cards as skipped, mark interacted as completed
        const newSkipped = new Set(skippedCards);
        const newCompleted = new Set(completedCards);
        currentSectionCards.forEach((card, idx) => {
          const globalIdx = sectionGlobalIndices[idx];
          if (!newCompleted.has(globalIdx) && !newSkipped.has(globalIdx)) {
            if (cardIsInteracted(card, globalIdx)) {
              newCompleted.add(globalIdx);
            } else {
              newSkipped.add(globalIdx);
            }
          }
        });
        setSkippedCards(newSkipped);
        setCompletedCards(newCompleted);

        // Advance to next section or closure
        const currentSectionIdx = sections.findIndex((s) => s.id === currentSectionId);
        if (currentSectionIdx < sections.length - 1) {
          const nextSection = sections[currentSectionIdx + 1];
          const firstCardOfNextSection = cards.findIndex((c) => c.section === nextSection.id);
          if (firstCardOfNextSection !== -1) {
            setCurrentCardIndex(firstCardOfNextSection);
          }
        } else if (!hasCloseCallAttribute) {
          setCurrentCardIndex(cards.length);
        }
      }
    } else if (currentCardIndex < cards.length - 1) {
      // Move to next card
      setCurrentCardIndex((prev) => prev + 1);
    } else if (!hasCloseCallAttribute) {
      // Move to closure section (validation happens when selecting outcome)
      // Only if the script doesn't have a close_call attribute field
      setCurrentCardIndex(cards.length);
    }
    // If hasCloseCallAttribute is true and we're on the last card, do nothing
    // The user must use the close_call buttons within the card to complete
  }, [currentCardIndex, cards, formData, isOnClosureSection, hasCloseCallAttribute, triggerIdentifyCallerAutoCreate, scrollableView, currentSectionId, currentSectionQuestions, sections, completedCards, skippedCards]);

  const handleBack = useCallback(() => {
    // Auto-create lead if IdentifyCaller has unsaved changes
    triggerIdentifyCallerAutoCreate();

    if (isOnClosureSection) {
      // Go back to last card
      setCurrentCardIndex(cards.length - 1);
    } else if (currentCardIndex > 0) {
      setCurrentCardIndex((prev) => prev - 1);
    }
  }, [currentCardIndex, isOnClosureSection, cards.length, triggerIdentifyCallerAutoCreate]);

  const handleSkip = useCallback(() => {
    // Auto-create lead if IdentifyCaller has unsaved changes
    triggerIdentifyCallerAutoCreate();

    setSkippedCards((prev) => new Set([...prev, currentCardIndex]));
    if (currentCardIndex < cards.length - 1) {
      setCurrentCardIndex((prev) => prev + 1);
    }
  }, [currentCardIndex, cards.length, triggerIdentifyCallerAutoCreate]);

  const handleSave = useCallback(() => {
    setSaveStatus('saving');
    onSave?.({ formData, cardNotes, callNotes, currentCardIndex });
    setTimeout(() => setSaveStatus('saved'), 1000);
  }, [formData, cardNotes, callNotes, currentCardIndex, onSave]);

  const handleSectionClick = useCallback((sectionId) => {
    // Handle closure section - allow free navigation like other sections
    if (sectionId === closureSectionId) {
      setCurrentCardIndex(cards.length);
      return;
    }

    const firstCardOfSection = cards.findIndex((c) => c.section === sectionId);
    if (firstCardOfSection !== -1) {
      setCurrentCardIndex(firstCardOfSection);
      // In scrollable mode, scroll the first card of the section into view
      if (scrollableView) {
        setTimeout(() => {
          const cardRef = scrollableCardRefs.current[firstCardOfSection];
          if (cardRef) {
            cardRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 50);
      }
    }
  }, [cards, closureSectionId, scrollableView]);

  const handleQuestionClick = useCallback((questionIndex) => {
    const cardIndex = sectionCardGlobalIndices[questionIndex] ?? cards.indexOf(currentSectionQuestions[questionIndex]);
    if (cardIndex !== -1) {
      setCurrentCardIndex(cardIndex);
      // In scrollable mode, scroll the clicked question card into view
      if (scrollableView) {
        setTimeout(() => {
          const cardRef = scrollableCardRefs.current[cardIndex];
          if (cardRef) {
            cardRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 50);
      }
    }
  }, [cards, currentSectionQuestions, sectionCardGlobalIndices, scrollableView]);

  // Helper to resolve merge tags in text
  const resolveMergeTags = useCallback((text) => {
    if (!text) return text;
    return text.replace(/\$([a-zA-Z0-9_]+)/g, (match, key) => {
      const value = mergeTagData[key];
      return value !== undefined ? value : match;
    });
  }, [mergeTagData]);

  // Helper to format all card notes into HTML for better rendering
  const formatCardNotesToCallNotes = useCallback((notesObj) => {
    const noteEntries = Object.entries(notesObj).filter(([, note]) => note?.trim());
    if (noteEntries.length === 0) return '';

    // Group notes by section
    const notesBySection = {};
    noteEntries.forEach(([cardIndexStr, note]) => {
      const cardIndex = parseInt(cardIndexStr, 10);
      const card = cards[cardIndex];
      if (card) {
        const sectionId = card.section || 1;
        const section = sections.find((s) => s.id === sectionId);
        const sectionName = section?.name || `Section ${sectionId}`;

        if (!notesBySection[sectionId]) {
          notesBySection[sectionId] = {
            name: sectionName,
            notes: []
          };
        }
        // Resolve merge tags in both title and note content
        notesBySection[sectionId].notes.push({
          questionTitle: resolveMergeTags(card.title) || `Question ${cardIndex + 1}`,
          note: resolveMergeTags(note.trim())
        });
      }
    });

    // Format notes as HTML
    const formattedParts = Object.entries(notesBySection).map(([, sectionData]) => {
      const sectionNotes = sectionData.notes.map((n) =>
        `<div style="margin-bottom: 8px;"><strong>${n.questionTitle}</strong><br/><span style="color: #637381;">${n.note.replace(/\n/g, '<br/>')}</span></div>`
      ).join('');
      return `<div style="margin-bottom: 12px;"><div style="font-weight: 600; color: #212B36; margin-bottom: 6px; border-bottom: 1px solid #E0E0E0; padding-bottom: 4px;">${sectionData.name}</div>${sectionNotes}</div>`;
    });

    return formattedParts.join('');
  }, [cards, sections, resolveMergeTags]);

  const handleNoteChange = useCallback((note) => {
    // Calculate formatted notes first for auto-save
    const updatedCardNotes = { ...cardNotes, [currentCardIndex]: note };
    const formattedNotes = formatCardNotesToCallNotes(updatedCardNotes);

    setCardNotes(updatedCardNotes);
    setCallNotes(formattedNotes);

    // Trigger auto-save when notes change (if lead is selected)
    // Pass the HTML-formatted notes to the API
    if (formData.lead_id || followLead?.id) {
      setSaveStatus('saving');
      debouncedAutoSave({ callNotes: formattedNotes, lead_id: formData.lead_id || followLead?.id });
    }
  }, [currentCardIndex, cardNotes, formatCardNotesToCallNotes, formData.lead_id, followLead?.id, debouncedAutoSave]);

  // Note change handler for scrollable view — takes explicit card index
  const handleNoteChangeForCard = useCallback((cardIdx, note) => {
    const updatedCardNotes = { ...cardNotes, [cardIdx]: note };
    const formattedNotes = formatCardNotesToCallNotes(updatedCardNotes);

    setCardNotes(updatedCardNotes);
    setCallNotes(formattedNotes);

    if (formData.lead_id || followLead?.id) {
      setSaveStatus('saving');
      debouncedAutoSave({ callNotes: formattedNotes, lead_id: formData.lead_id || followLead?.id });
    }
  }, [cardNotes, formatCardNotesToCallNotes, formData.lead_id, followLead?.id, debouncedAutoSave]);

  const handleOutcomeSelect = useCallback((outcome) => {
    // Validate required fields before proceeding
    const errors = validateRequiredFields();
    if (errors.length > 0) {
      setValidationErrors(errors);
      // Navigate to the first card with an error
      setCurrentCardIndex(errors[0].cardIndex);
      return;
    }

    // Use formDataRef for latest values to avoid stale closure (Priority 4 fix)
    const currentFormData = formDataRef.current;

    // Store the outcome in form data
    const updatedFormData = { ...currentFormData, callOutcome: outcome };
    setFormData(updatedFormData);

    // Do a final save before triggering the action (not debounced)
    // Clear any pending debounced save
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Trigger immediate save with outcome included
    const leadId = currentFormData.lead_id || followLead?.id;

    // PRIORITY 1 FIX: Block submission if no lead is associated
    if (!leadId) {
      enqueueSnackbar('Please identify or create the caller before submitting.', { variant: 'error', autoHideDuration: 5000 });
      return;
    }

    if (leadId && practiceId) {
      // Pass callNotes to autoSaveLeadDetails so notes API is called there
      autoSaveLeadDetails({ ...updatedFormData, callOutcome: outcome, lead_id: leadId, callNotes });

      // Save final call notes if not already saved (handles case where outcome-specific note type is needed)
      const currentNotesContent = callNotes?.trim() || '';
      if (currentNotesContent && currentNotesContent !== notesRef.current.lastSavedContent) {
        const treatmentId = currentFormData.treatment_id || activeCallScriptTreatment?.id;
        const noteType = outcome === 'submit' ? 'UNABLE_TO_CONTACT' : 'FOLLOW_UP';
        const notesData = {
          lead: leadId,
          additional_notes: currentNotesContent,
          note: noteType,
        };
        if (treatmentId) {
          notesData.treatment = treatmentId;
        }

        // Use PATCH if we have an existing note ID, otherwise POST to create new note
        if (notesRef.current.noteId) {
          const updateNotesData = {
            ...notesData,
            id: notesRef.current.noteId, // Include note ID in the request body
          };
          dispatch(updateNotes(updateNotesData, practiceId, notesRef.current.noteId, () => {
            notesRef.current.lastSavedContent = currentNotesContent;
          }, 'callScript'));
        } else {
          dispatch(addNotes(notesData, practiceId, (result) => {
            notesRef.current.lastSavedContent = currentNotesContent;
            if (result?.success || result?.status === 201) {
              notesRef.current.noteId = result?.data?.id || result?.id;
            }
          }, 'callScript'));
        }
      }
    }

    // Clear the local draft on successful submission
    clearCurrentDraft();

    // Call the appropriate callback and close the dialog
    if (outcome === 'booked') {
      onAppointmentBooked?.({ formData: updatedFormData, cardNotes, callNotes });
      // Close the call script dialog so book appointment modal is visible
      onClose?.();
    } else if (outcome === 'not-booked') {
      onAppointmentNotBooked?.({ formData: updatedFormData, cardNotes, callNotes });
      // Close the call script dialog
      onClose?.();
    } else {
      // 'submit' - For INBOUND/TCO scripts
      onComplete?.({ formData: updatedFormData, cardNotes, callNotes });
    }
  }, [formData, cardNotes, callNotes, onAppointmentBooked, onAppointmentNotBooked, onComplete, onClose, validateRequiredFields, followLead?.id, followLead?.lead_treatments, practiceId, autoSaveLeadDetails, enqueueSnackbar, clearCurrentDraft, activeCallScriptTreatment]);

  // Handle confirm speaker selection (for outbound flow)
  const handleConfirmSpeakerSelect = useCallback((selectedValue) => {
    if (previewMode) return;

    switch (selectedValue) {
      case 'yes_speaking':
        // Auto-proceed to next card
        if (currentCardIndex < cards.length - 1) {
          setCurrentCardIndex(currentCardIndex + 1);
        }
        break;

      case 'wrong_person':
        // Show wrong person modal
        setWrongPersonNote('');
        setShowWrongPersonModal(true);
        break;

      case 'no_answer':
        // Trigger the existing "Unable to contact" flow via onUnableToContact callback
        // Parent handles closing, so don't call onClose here
        onUnableToContact?.({ formData, cardNotes, callNotes });
        break;

      default:
        break;
    }
  }, [previewMode, currentCardIndex, cards.length, onUnableToContact, formData, cardNotes, callNotes]);

  // Handle wrong person modal submission
  const handleWrongPersonSubmit = useCallback(() => {
    const leadId = formData.lead_id || followLead?.id;
    const treatmentId = formData.treatment_id || activeCallScriptTreatment?.id;

    // Close the modal first
    setShowWrongPersonModal(false);

    if (leadId && practiceId) {
      // Add auto-note for wrong phone number
      const autoNote = 'Wrong phone number linked to lead';
      const fullNote = wrongPersonNote
        ? `${autoNote}\n\nAdditional notes: ${wrongPersonNote}`
        : autoNote;

      const notesData = {
        lead: leadId,
        additional_notes: fullNote,
        note: 'UNABLE_TO_CONTACT',
      };
      if (treatmentId) {
        notesData.treatment = treatmentId;
      }

      // Dispatch notes and close everything after completion
      // Use onComplete instead of onClose to trigger 'finish' flow which closes all dialogs
      dispatch(addNotes(notesData, practiceId, () => {
        onComplete?.({ leadId, wrongPerson: true });
      }, 'callScript'));
    } else {
      // No lead/practice, just close everything
      onComplete?.({ wrongPerson: true });
    }
  }, [formData.lead_id, formData.treatment_id, followLead?.id, followLead?.lead_treatments, practiceId, wrongPersonNote, onComplete, activeCallScriptTreatment]);

  // Handle wrong person modal cancel
  const handleWrongPersonCancel = useCallback(() => {
    setShowWrongPersonModal(false);
    // Clear the confirmSpeaker selection
    setFormData(prev => ({ ...prev, confirmSpeaker: '' }));
  }, []);

  // AI Summary handlers
  const handleGenerateAISummary = useCallback(async () => {
    // Step 0: Validate required fields before generating summary
    const errors = validateRequiredFields();
    if (errors.length > 0) {
      setValidationErrors(errors);
      // Navigate to the first card with an error
      setCurrentCardIndex(errors[0].cardIndex);
      return;
    }

    // Step 1: Calculate call duration
    const endTime = new Date();
    const durationMs = endTime - callStartTime;
    const durationSeconds = Math.floor(durationMs / 1000);
    setCallDuration(durationSeconds);

    // Step 2: Implicit save - save the call script first
    const leadId = formData.lead_id || followLead?.id;
    if (leadId && practiceId) {
      // Trigger save with current form data
      autoSaveLeadDetails({ ...formData, lead_id: leadId, callNotes });
    }

    // Step 3: Open modal and set loading
    setAiSummaryModalOpen(true);
    setAiSummaryLoading(true);

    // Step 4: Call AI summary API with 3 parallel requests
    try {
      const aiResult = await generateAICallSummary({
        practiceId,
        formData,
        cardNotes,
        callNotes,
        cards,
        leadInfo: followLead,
        aiConfig, // Pass AI config with practiceInfo, treatmentInfo, pricingInfo
        emailTemplates: emailTemplates?.results || [],
        whatsappTemplates: whatsappTemplates || [],
        appointmentMethods: appointmentMethod || [],
        appointmentTypes: appointmentType || [],
        practiceUsers: practiceUser?.results || [],
        treatments: followLead?.lead_treatments || [],
      });

      setAiSummaryLoading(false);
      setAiSummaryData(aiResult);
    } catch (error) {
      console.error('AI Summary generation failed:', error);
      setAiSummaryLoading(false);
      // Set fallback data on error
      setAiSummaryData({
        classification: 'Unable to classify',
        readinessScore: 50,
        budget: 'Not analyzed',
        availability: 'Not analyzed',
        keyQuote: '',
        concerns: [],
        nextSteps: [],
        tags: [],
        sentiment: 'Neutral',
        aiSummaryText: 'AI analysis could not be completed. Please review the call script data manually.',
        hasErrors: true,
        errors: { general: error.message },
      });
    }
  }, [formData, followLead, practiceId, callNotes, autoSaveLeadDetails, callStartTime, validateRequiredFields, cardNotes, cards, emailTemplates, whatsappTemplates, appointmentMethod, appointmentType, practiceUser, aiConfig]);

  // Handle back to script - close modal only
  const handleBackToScript = useCallback(() => {
    setAiSummaryModalOpen(false);
    setAiSummaryLoading(false);
  }, []);

  // Handle close all - close modal and call script
  const handleCloseAll = useCallback(() => {
    setAiSummaryModalOpen(false);
    setAiSummaryLoading(false);
    setAiSummaryData(null);
    // Close the call script dialog
    onClose?.();
  }, [onClose]);

  // AI Booking Wizard handlers
  const handleOpenAIBookingWizard = useCallback(() => {
    setAiBookingWizardOpen(true);
  }, []);

  const handleCloseAIBookingWizard = useCallback(() => {
    setAiBookingWizardOpen(false);
  }, []);

  const handleAIBookAppointment = useCallback((appointmentData) => {
    if (!practiceId || !followLead?.id) return;

    setAiBookingLoading(true);
    dispatch(addAppointment(
      appointmentData,
      practiceId,
      followLead.id,
      () => {
        // Success callback
        setAiBookingLoading(false);
        setAiBookingWizardOpen(false);
        // Track appointment booked status for AI summary display
        setAppointmentBooked(true);
        setBookedAppointmentData({
          datetime: appointmentData.datetime,
          treatment: appointmentData.treatment_name,
          method: appointmentData.method,
          type: appointmentData.type,
        });

        // The wizard collects payment_option/payment_amount/payment_method
        // itself (deposit/consultation fee/etc.) — the appointment above never
        // records that payment, so without this it is silently lost. Reuses
        // the SAME thunk (leadTreatmentPayment) the standard booking flow uses,
        // so backend behaviour (paid_at, Stripe checkout) is identical. Fired
        // after the appointment is confirmed and never rolls it back on failure —
        // saveAIBookingPayment surfaces the backend error via updateError itself.
        if (appointmentData.payment_option && appointmentData.payment_option !== 'No') {
          dispatch(saveAIBookingPayment(
            practiceId,
            followLead.id,
            appointmentData,
            followLead?.lead_contact
          ));
        }
      },
      'CONSULTATION_BOOKED'
    ));
  }, [practiceId, followLead?.id, followLead?.lead_contact]);

  // Handler for adding follow-up reminder from AI suggestion
  const handleAddReminder = useCallback((reminderData) => {
    if (!followLead?.id) return;

    // Create a task/reminder based on AI recommendation
    // This will add a note with reminder info to the lead
    const reminderNote = `Follow-up Reminder: ${reminderData.reminderNote || 'Follow up with patient'}
Date: ${reminderData.reminderDate || 'TBD'}
Time: ${reminderData.reminderTime || 'TBD'}
Priority: ${reminderData.priority || 'medium'}`;

    dispatch(addNotes(
      { note: reminderNote },
      practiceId,
      followLead.id,
      () => {
        // Success - reminder added as a note
      }
    ));
  }, [followLead?.id, practiceId]);

  // Handler for sending WhatsApp message from AI suggestion
  // eslint-disable-next-line no-unused-vars
  const handleSendWhatsApp = useCallback((_whatsAppData) => {
    // Stub: In future, this could open a WhatsApp send modal with pre-filled template
    // The WhatsApp sending is complex and requires the WhatsApp business account setup
  }, []);

  // Handler for saving tags from AI Summary
  // This creates tags if they don't exist, then assigns them to the lead
  const handleSaveTags = useCallback((tagsToSave) => {
    if (!followLead?.id || !practiceId || !tagsToSave || tagsToSave.length === 0) {
      return;
    }

    // Get existing tag names (lowercase for comparison)
    const existingTagNames = (existingTags?.results || []).map(t => t.name?.toLowerCase());

    // Process each tag
    tagsToSave.forEach((tag) => {
      const tagName = tag.name?.trim();
      if (!tagName) return;

      // Determine tag color based on type
      let tagColor = '#8E33FF'; // Default purple for custom tags
      if (tag.type === 'classification') {
        // Classification tags get specific colors
        const lowerName = tagName.toLowerCase();
        if (lowerName.includes('hot')) tagColor = '#FF5630';
        else if (lowerName.includes('warm')) tagColor = '#FFAB00';
        else if (lowerName.includes('cold')) tagColor = '#00B8D9';
        else if (lowerName.includes('not interested')) tagColor = '#637381';
      } else if (tag.type === 'treatment') {
        tagColor = '#00B8D9'; // Info blue for treatment
      } else if (tag.type === 'source') {
        tagColor = '#8E33FF'; // Purple for source
      } else if (tag.type === 'payment') {
        tagColor = '#FFAB00'; // Warning yellow for payment
      } else if (tag.type === 'urgency') {
        tagColor = '#FF5630'; // Red for urgency
      }

      const tagData = { name: tagName, colour: tagColor };

      // Check if tag already exists in the system
      const tagExists = existingTagNames.includes(tagName.toLowerCase());

      if (tagExists) {
        // Tag exists - just assign it to the lead
        dispatch(createTagItem(
          tagData,
          practiceId,
          followLead.id,
          () => {},
          'ai-summary'
        ));
      } else {
        // Tag doesn't exist - create it first (which will also assign it)
        dispatch(createCustomTagItem(
          tagData,
          practiceId,
          followLead.id,
          () => {},
          null // null for LEAD category
        ));
      }
    });
  }, [followLead?.id, practiceId, existingTags]);

  // Handler for saving AI notes as JSON note
  const handleSaveAINotes = useCallback((aiNotesData) => {
    if (!followLead?.id || !practiceId || !aiNotesData) {
      return;
    }

    // Create note data with JSON stringified AI notes
    const noteData = {
      lead: followLead.id,
      additional_notes: JSON.stringify(aiNotesData),
      note: 'OTHER', // Use OTHER type for AI notes
      contacted_via: 'PHONE', // Default to phone for call script
      treatment: activeCallScriptTreatment?.id || null,
    };

    dispatch(addNotes(
      noteData,
      practiceId,
      followLead.id,
      () => {},
      'callScript'
    ));
  }, [followLead?.id, followLead?.lead_treatments, practiceId, activeCallScriptTreatment]);

  // Special field handlers
  const handleLeadSelect = useCallback((lead) => {
    if (lead) {
      const leadName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim();
      const leadActiveTreatment = resolveCallScriptTreatment(lead, activeTreatmentId);
      const leadTreatmentName = getTreatmentName(leadActiveTreatment?.treatment);

      // Check current form treatment BEFORE state update (using ref for latest value)
      const currentFormTreatment = formDataRef.current.treatment;
      const formHasTreatment = currentFormTreatment && currentFormTreatment.trim() !== '';
      const leadHasTreatment = !!leadTreatmentName;

      // Determine if we need to add form treatment to the lead
      // (form has treatment selected but lead doesn't have one)
      const shouldAddFormTreatmentToLead = formHasTreatment && !leadHasTreatment;
      const formTreatmentToAdd = shouldAddFormTreatmentToLead ? {
        treatment: formDataRef.current.treatment,
        treatmentValue: formDataRef.current.treatmentValue || '',
        teethAmount: formDataRef.current.teethAmount ?? null,
      } : null;

      // Determine effective treatment (form treatment takes priority if lead has none)
      let effectiveTreatment = '';
      if (leadHasTreatment) {
        effectiveTreatment = leadTreatmentName;
      } else if (formHasTreatment) {
        effectiveTreatment = currentFormTreatment;
      }

      // Auto-populate form fields from selected lead
      // IMPORTANT: Preserve form-selected treatment if lead doesn't have one (treatment has higher priority)
      setFormData((prev) => {
        // Determine which treatment to use
        let finalTreatment;
        let finalTreatmentValue;
        let finalTreatmentId;
        let finalTeethAmount;
        let finalTreatmentInterest;
        let finalTreatmentInterestValue;
        let finalTreatmentInterestTeeth;

        if (leadHasTreatment) {
          // Lead has treatment - use lead's treatment
          finalTreatment = leadTreatmentName;
          finalTreatmentValue = leadActiveTreatment?.price || '';
          finalTreatmentId = leadActiveTreatment?.id || '';
          finalTeethAmount = leadActiveTreatment?.leadtreatment_specification?.teeth_count ?? null;
          finalTreatmentInterest = leadTreatmentName;
          finalTreatmentInterestValue = leadActiveTreatment?.price || '';
          finalTreatmentInterestTeeth = leadActiveTreatment?.leadtreatment_specification?.teeth_count ?? null;
        } else if (formHasTreatment) {
          // Lead has no treatment but form does - preserve form treatment
          finalTreatment = prev.treatment;
          finalTreatmentValue = prev.treatmentValue || '';
          finalTreatmentId = ''; // Will be set after adding treatment to lead
          finalTeethAmount = prev.teethAmount ?? null;
          finalTreatmentInterest = prev.treatmentInterest || prev.treatment;
          finalTreatmentInterestValue = prev.treatmentInterestValue || prev.treatmentValue || '';
          finalTreatmentInterestTeeth = prev.treatmentInterestTeeth ?? prev.teethAmount ?? null;
        } else {
          // Neither has treatment
          finalTreatment = '';
          finalTreatmentValue = '';
          finalTreatmentId = '';
          finalTeethAmount = null;
          finalTreatmentInterest = '';
          finalTreatmentInterestValue = '';
          finalTreatmentInterestTeeth = null;
        }

        return {
          ...prev,
          lead_id: lead.id || '',
          firstName: lead.first_name || '',
          lastName: lead.last_name || '',
          leadName,
          title: lead.salutation || '',
          date_of_birth: lead.date_of_birth || lead.dob || '',
          dob: lead.dob || lead.date_of_birth || '',
          phone: lead.lead_contact?.phone || '',
          phone_number: lead.lead_contact?.phone || '',
          email: lead.lead_contact?.email || '',
          treatment: finalTreatment,
          treatmentValue: finalTreatmentValue,
          treatment_id: finalTreatmentId,
          teethAmount: finalTeethAmount,
          treatmentInterest: finalTreatmentInterest,
          treatmentInterestValue: finalTreatmentInterestValue,
          treatmentInterestTeeth: finalTreatmentInterestTeeth,
          address_line_1: lead.lead_address?.address_line_1 || '',
          address_line_2: lead.lead_address?.address_line_2 || '',
          city: lead.lead_address?.city || '',
          county: lead.lead_address?.county || '',
          postcode: lead.lead_address?.postcode || '',
          leadType: lead.lead_source?.type || '',
          referral: lead.lead_source?.referral || '',
          preferredContactMethod: lead.lead_contact?.preferred_contact_method || '',
        };
      });

      // Show loading modal when we have a treatment (minimum 3 seconds)
      if (effectiveTreatment && scriptCategory === 'INBOUND') {
        setTreatmentSwitchingModal({
          open: true,
          message: `Loading ${leadName}'s data with ${effectiveTreatment} script...`,
        });
        setTimeout(() => {
          setTreatmentSwitchingModal({ open: false, message: '' });
        }, 3000);
      }

      setSaveStatus('saving');
      setTimeout(() => setSaveStatus('saved'), 1000);

      // Reset submission state for the new lead (previous submission was for a different lead)
      formRef.current = { inProgress: false, formId: null, fieldMap: {}, fieldSubmissionMap: {}, pendingFormData: null };
      // Reset user-changed flag so initial effect can work if needed
      treatmentUserChangedRef.current = false;

      // If form had a treatment but lead didn't, add the treatment to the lead
      if (shouldAddFormTreatmentToLead && formTreatmentToAdd && lead.id && practiceId) {
        const detail = {
          treatment: formTreatmentToAdd.treatment,
          custom_price: formTreatmentToAdd.treatmentValue,
          teethAmount: formTreatmentToAdd.teethAmount,
        };
        dispatch(addTreatment(
          detail,
          practiceId,
          lead.id,
          (result) => {
            if (result?.success || result?.treatment_id) {
              // Store the new treatment_id for future updates
              setFormData((prev) => ({ ...prev, treatment_id: result?.treatment_id || result?.data?.id }));
            }
          },
          { needToUpdate: detail.teethAmount || detail.teethAmount === 0, teeth: detail.teethAmount || 0 }
        ));
      }

      // Fetch treatment-specific scripts for dynamic merging (INBOUND scripts only)
      // Use effective treatment (form treatment takes priority over lead treatment)
      if (effectiveTreatment && practiceId && scriptCategory === 'INBOUND') {
        // Clear previous treatment scripts before fetching new ones
        setAdditionalScriptCards([]);
        setAdditionalScriptSections([]);
        setSelectedTemplateId(null);
        setTreatmentScriptsLoaded(false);
        requestedTreatmentRef.current = effectiveTreatment;
        // Fetch INBOUND scripts matching this treatment
        dispatch(getCallScriptSection({
          practiceId,
          treatment: effectiveTreatment,
          isTemplate: false,
          category: 'INBOUND',
        }));
      }
    }
  }, [practiceId, scriptCategory, dispatch, activeTreatmentId]);

  const handleTreatmentClick = useCallback((event) => {
    setTreatmentAnchorEl(event.currentTarget);
    setTreatmentPopoverOpen(true);
  }, []);

  const handleTreatmentClose = useCallback(() => {
    setTreatmentPopoverOpen(false);
    setTreatmentAnchorEl(null);
  }, []);

  // Helper to save treatment changes to the backend
  const saveTreatmentChanges = useCallback((updatedTreatmentData) => {
    const leadId = formData.lead_id || followLead?.id;
    // IMPORTANT: Always use the lead_treatment ID (from formData or followLead), NOT any ID passed in updatedTreatmentData
    // The selectedTreatmentId from TreatmentEditPopover is the practice treatment catalog ID, not the lead_treatment ID
    const treatmentId = formData.treatment_id || activeCallScriptTreatment?.id;

    if (!leadId || !practiceId) {
      setSaveStatus('idle');
      return;
    }

    const detail = {
      treatment: updatedTreatmentData.treatment ?? formData.treatment,
      custom_price: updatedTreatmentData.treatmentValue ?? formData.treatmentValue,
      teethAmount: updatedTreatmentData.teethAmount ?? formData.teethAmount,
    };

    // If we have a treatment_id, update existing treatment; otherwise add new treatment
    if (treatmentId) {
      dispatch(UpdateTreatmentV3(
        { detail, lead_id: leadId, treatment_id: treatmentId },
        'autosave',
        (result) => {
          if (result?.success) {
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 2000);
          } else {
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 2000);
          }
        }
      ));
    } else if (detail.treatment) {
      // Only add treatment if we have a treatment name
      dispatch(addTreatment(
        detail,
        practiceId,
        leadId,
        (result) => {
          if (result?.success || result?.treatment_id) {
            // Store the new treatment_id for future updates
            setFormData((prev) => ({ ...prev, treatment_id: result?.treatment_id || result?.data?.id }));
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 2000);
          } else {
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 2000);
          }
        },
        { needToUpdate: detail.teethAmount || detail.teethAmount === 0, teeth: detail.teethAmount || 0 }
      ));
    }
  }, [formData, followLead, practiceId, activeCallScriptTreatment]);

  const handleSaveTreatment = useCallback((treatmentInfo) => {
    // Note: treatmentInfo.selectedTreatmentId is the PRACTICE treatment catalog ID
    // We should NOT use this for API calls - we use the existing lead_treatment ID instead
    // Only pass treatment name and price to saveTreatmentChanges
    const newTreatment = treatmentInfo.selectedTreatment || '';
    const oldTreatment = formData.treatment || '';
    const treatmentChanged = newTreatment !== oldTreatment;

    const updatedData = {
      treatment: newTreatment,
      treatmentValue: treatmentInfo.treatmentPrice || '',
      teethAmount: treatmentInfo.teethAmount || null,
      // Also sync treatmentInterest fields (for reason_for_calling attribute)
      treatmentInterest: newTreatment,
      treatmentInterestValue: treatmentInfo.treatmentPrice || '',
      treatmentInterestTeeth: treatmentInfo.teethAmount || null,
    };

    // PRECOMPUTATION: Check if script would actually change before triggering loading
    // This avoids unnecessary loading modal and step reset when selecting a treatment
    // that maps to the same script as the current one

    // First, check if new treatment is already served by the current script
    // This is the most reliable check - if current script lists this treatment, no change needed
    const newTreatmentLower = newTreatment.toLowerCase().trim();
    const isServedByCurrentScript = currentScriptTreatments.has(newTreatmentLower);
    const currentScriptId = effectiveScriptData?.id || selectedTemplateId || null;

    // If new treatment is already in current script's treatments list, no change needed
    // Otherwise, fall back to prediction logic
    let scriptWouldChange = false;
    if (treatmentChanged) {
      if (isServedByCurrentScript) {
        // Current script already serves this treatment - no change needed
        scriptWouldChange = false;
      } else {
        // Current script doesn't serve this treatment - check if a different script would be used
        const predictedScriptId = getScriptIdForTreatment(newTreatment);
        // Script would change if:
        // 1. New treatment has a specific script AND it's different from current
        // 2. OR new treatment has NO specific script (null) AND we're currently on an override (will fall back to base)
        scriptWouldChange = predictedScriptId !== null
          ? predictedScriptId !== currentScriptId
          : overrideBaseScript !== null;
      }
    }

    // If treatment name changed but script would be the same, just update form data
    // (no loading modal, no step reset, no API call for scripts)
    if (treatmentChanged && !scriptWouldChange) {
      // Mark that treatment was user-changed to prevent initial useEffect from re-fetching
      treatmentUserChangedRef.current = true;
      // Skip script processing in useEffect - prevents step reset and blink
      skipScriptProcessingRef.current = true;

      // Just update form data - no script loading needed
      setFormData((prev) => ({
        ...prev,
        treatment: updatedData.treatment,
        treatmentValue: updatedData.treatmentValue,
        teethAmount: updatedData.teethAmount,
        treatmentInterest: updatedData.treatmentInterest,
        treatmentInterestValue: updatedData.treatmentInterestValue,
        treatmentInterestTeeth: updatedData.treatmentInterestTeeth,
      }));

      handleTreatmentClose();
      setSaveStatus('saving');
      saveTreatmentChanges(updatedData);
      return;
    }

    // If treatment actually changed AND script would change, do full reset and fetch
    if (scriptWouldChange) {
      // Mark that treatment was user-changed to prevent initial useEffect from re-fetching stale scripts
      treatmentUserChangedRef.current = true;

      // Store current script ID before clearing - modal will only show if script actually changes
      previousScriptIdRef.current = currentScriptId;

      // 1. Clear previous treatment's override script and stale API data, then show loading
      setOverrideBaseScript(null);
      setAdditionalScriptCards([]);
      setAdditionalScriptSections([]);
      setSelectedTemplateId(null);
      dispatch(setCallScriptSection(null));
      setTreatmentScriptsLoaded(false);

      // 2. Reset submission state so next save creates a NEW submission (POST) instead of PATCH
      formRef.current = { inProgress: false, formId: null, fieldMap: {}, fieldSubmissionMap: {}, pendingFormData: null };

      // 3. Update formData with new treatment values (step 7 will clear script-specific fields)
      setFormData((prev) => ({
        ...prev,
        treatment: updatedData.treatment,
        treatmentValue: updatedData.treatmentValue,
        teethAmount: updatedData.teethAmount,
        treatmentInterest: updatedData.treatmentInterest,
        treatmentInterestValue: updatedData.treatmentInterestValue,
        treatmentInterestTeeth: updatedData.treatmentInterestTeeth,
      }));

      // 7. Fetch new treatment-specific scripts if we have a new treatment name
      if (newTreatment && practiceId) {
        // Clear form data for script-specific fields (entire script will be replaced)
        // Preserve only common lead fields that are shared across all scripts
        const preserveFields = new Set([
          // Lead identity fields
          'lead_id', 'firstName', 'lastName', 'leadName', 'title',
          'phone', 'phone_number', 'email', 'treatment', 'treatmentValue',
          'treatment_id', 'teethAmount', 'dob', 'date_of_birth', 'address_line_1', 'address_line_2',
          'city', 'county', 'postcode', 'leadType', 'referral',
          'bookedWith', 'preferredContactMethod', 'isRegister', 'callOutcome', 'interestLevel',
          'treatmentInterest', 'treatmentInterestValue', 'treatmentInterestTeeth',
          'completedBy', 'answerTheCallCompletedBy',
          // Identify caller fields (special attribute)
          'callerType', 'callerFirstName', 'callerLastName',
          'selectedLeadId', 'selectedLeadData', 'callerLeadCreated',
          // Confirm speaker field
          'confirmSpeaker',
          // Reason for call field
          'reasonForCall',
        ]);
        setFormData((prev) => {
          const preserved = {};
          Object.keys(prev).forEach((key) => {
            if (preserveFields.has(key)) {
              preserved[key] = prev[key];
            }
          });
          return preserved;
        });

        // For INBOUND scripts: use prefetched inboundScriptsData directly (faster, more reliable)
        // For other categories: fetch via API
        if (scriptCategory === 'INBOUND' && inboundScriptsData?.results) {
          // Find the script that serves this treatment from prefetched data
          const treatmentLower = newTreatment.toLowerCase().trim();
          const matchingScripts = inboundScriptsData.results.filter((script) => {
            if (script.is_default) return false;
            // Ensure script category matches INBOUND (extra safety check)
            if (script.category && script.category !== 'INBOUND') return false;
            const scriptTreatments = script.treatments || [];
            return scriptTreatments.some((t) => {
              const itemName = (t.treatment_item?.name || t.name || '').toLowerCase().trim();
              return itemName === treatmentLower;
            });
          });

          if (matchingScripts.length > 0) {
            // Select the most specific script (fewest treatments)
            const selectedScript = matchingScripts.length > 1
              ? matchingScripts.reduce((best, script) => {
                const bestCount = best.treatments?.length || Infinity;
                const scriptCount = script.treatments?.length || Infinity;
                return scriptCount < bestCount ? script : best;
              }, matchingScripts[0])
              : matchingScripts[0];

            // Transform and set as override
            const transformedData = transformScriptData(selectedScript, 'inbound');
            setOverrideBaseScript(transformedData);
            setSelectedTemplateId(selectedScript.id);
            setTreatmentScriptsLoaded(true);

            // Show treatment switching modal
            setTreatmentSwitchingModal({
              open: true,
              message: `Switching to ${newTreatment} script...`,
            });
            setTimeout(() => {
              setTreatmentSwitchingModal({ open: false, message: '' });
            }, 3000);

            // Reset to first step
            setCurrentCardIndex(0);
            setCompletedCards(new Set());
            setSkippedCards(new Set());
          } else {
            // No matching script found - stay on current script
            setTreatmentScriptsLoaded(true);
          }
        } else {
          // For OUTBOUND/TCO: use API
          requestedTreatmentRef.current = newTreatment;
          dispatch(getCallScriptSection({
            practiceId,
            treatment: newTreatment,
            isTemplate: false,
            category: scriptCategory,
          }));
        }
      }
    } else {
      // Treatment name didn't change (only price/teeth changed) - just update form data
      setFormData((prev) => ({
        ...prev,
        treatment: updatedData.treatment,
        treatmentValue: updatedData.treatmentValue,
        teethAmount: updatedData.teethAmount,
        treatmentInterest: updatedData.treatmentInterest,
        treatmentInterestValue: updatedData.treatmentInterestValue,
        treatmentInterestTeeth: updatedData.treatmentInterestTeeth,
      }));
    }

    handleTreatmentClose();
    setSaveStatus('saving');
    // Save treatment changes to backend using existing lead_treatment ID
    saveTreatmentChanges(updatedData);
  }, [handleTreatmentClose, saveTreatmentChanges, formData.treatment, practiceId, scriptCategory, effectiveScriptData?.id, selectedTemplateId, getScriptIdForTreatment, currentScriptTreatments, overrideBaseScript]);

  // Separate timeout ref for treatment/teeth changes to avoid cancelling form auto-saves (Priority 6 fix)
  const treatmentSaveTimeoutRef = useRef(null);

  const handleTreatmentAmountChange = useCallback((value) => {
    setFormData((prev) => ({ ...prev, treatmentValue: value }));
    setSaveStatus('saving');
    // Debounce the API call to avoid too many requests while typing
    if (treatmentSaveTimeoutRef.current) {
      clearTimeout(treatmentSaveTimeoutRef.current);
    }
    treatmentSaveTimeoutRef.current = setTimeout(() => {
      saveTreatmentChanges({ treatmentValue: value });
    }, 1500);
  }, [saveTreatmentChanges]);

  const handleTeethChange = useCallback((event) => {
    const { value } = event.target;
    setFormData((prev) => ({ ...prev, teethAmount: value }));
    setSaveStatus('saving');
    // Debounce the API call to avoid too many requests while typing
    if (treatmentSaveTimeoutRef.current) {
      clearTimeout(treatmentSaveTimeoutRef.current);
    }
    treatmentSaveTimeoutRef.current = setTimeout(() => {
      saveTreatmentChanges({ teethAmount: value });
    }, 1500);
  }, [saveTreatmentChanges]);

  const handleAssigneeChange = useCallback((assignees) => {
    setAssignToState(assignees);
    setFormData((prev) => ({ ...prev, bookedWith: assignees }));
    setUserAutoSelected(false); // Clear auto-select when user manually changes
    setSaveStatus('saving');
    setTimeout(() => setSaveStatus('saved'), 1000);
  }, []);

  const handleAssigneePopupOpen = useCallback((event) => {
    setAssigneeAnchorEl(event.currentTarget);
    setAssigneePopupOpen(true);
  }, []);

  const handleAssigneePopupClose = useCallback(() => {
    setAssigneePopupOpen(false);
    setAssigneeAnchorEl(null);
    dispatch(clearPracticeUserData());
  }, []);

  // Initialize assignees from formData
  useEffect(() => {
    if (formData.bookedWith?.length > 0 && assignToState.length === 0) {
      setAssignToState(formData.bookedWith);
    }
  }, [formData.bookedWith, assignToState.length]);

  // Fetch practice users on mount for auto-select functionality
  // Include ADMIN role so admin users can also be auto-selected
  useEffect(() => {
    if (practiceId) {
      dispatch(getPracticeUserListing({ practiceId, role: 'TCO,USER,ADMIN' }));
    }
  }, [practiceId]);

  // Auto-select current user for assigneeSelector fields if match found in practice users list
  // completedBy is always auto-selected to current user
  // bookedWith/assignToState only auto-selected if no assignees already set
  useEffect(() => {
    // Need practice users and current user email to proceed
    if (!practiceUser?.results?.length || !currentAuthUser?.email) return;

    // Find matching user by email in the practice users list
    // Check TCO and USER roles first, but fall back to any matching user
    const validRoles = ['TCO', 'USER', 'ADMIN'];
    const matchingUser = practiceUser.results.find(
      (pu) => {
        const emailMatch = pu.user?.email?.toLowerCase() === currentAuthUser.email?.toLowerCase();
        const hasValidRole = validRoles.includes(pu.role?.toUpperCase());
        return emailMatch && hasValidRole;
      }
    ) || practiceUser.results.find(
      (pu) => pu.user?.email?.toLowerCase() === currentAuthUser.email?.toLowerCase()
    );

    // Auto-select if we found a matching user
    if (matchingUser) {
      // Always set completedBy and answerTheCallCompletedBy to current user if not already set
      setFormData((prev) => {
        const updates = {};
        // Set completedBy if not already set to this user
        if (prev.completedBy !== matchingUser.id) {
          updates.completedBy = matchingUser.id;
        }
        // Set answerTheCallCompletedBy if not already set to this user
        if (prev.answerTheCallCompletedBy !== matchingUser.id) {
          updates.answerTheCallCompletedBy = matchingUser.id;
        }
        // Only update if there are changes
        if (Object.keys(updates).length === 0) return prev;
        return { ...prev, ...updates };
      });

      // Only auto-select bookedWith if no existing assignees and not already auto-selected
      if (!userAutoSelected) {
        const hasExistingAssignees = followLead?.assignees?.length > 0;
        if (!hasExistingAssignees) {
          setAssignToState([matchingUser]);
          setFormData((prev) => ({ ...prev, bookedWith: [matchingUser] }));
        }
        setUserAutoSelected(true);
      }
    }
  }, [practiceUser?.results, currentAuthUser?.email, userAutoSelected, followLead?.assignees, overrideBaseScript]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger on input/textarea elements
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleNext();
      }
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handleSave]);

  // Helper to parse merge tags in text
  const parseMergeTags = useCallback((text) => {
    if (!text) return text;
    return text.replace(/\$[a-zA-Z0-9_]+/g, (match) => {
      const key = match.slice(1); // Remove '$'
      return Object.prototype.hasOwnProperty.call(mergeTagData, key)
        ? mergeTagData[key]
        : match;
    });
  }, [mergeTagData]);

  // Render card content based on type
  const renderCardContent = (card, cardIndexOverride) => {
    if (!card) return null;

    const elements = [];

    // Render coaching hint above card content (from metadata.hints.coaching).
    // Hidden when the agent has toggled the "Coaching" chip off (per-user preference).
    if (card.metadata?.hints?.coaching && !hiddenHintTypes.includes('coaching')) {
      elements.push(
        <CoachingBlock
          key="meta-coaching"
          text={card.metadata.hints.coaching}
        />
      );
    }

    // Render each element in the card
    card.elements?.forEach((element, idx) => {
      switch (element.type) {
        case 'say': {
          const { text: sayText, hint: sayHint } = extractHint(element.text);
          elements.push(
            <SayBlock
              key={idx}
              text={sayText}
              tag={element.tag || 'Say'}
              variant={element.variant || 'primary'}
              mergeTagData={mergeTagData}
            />
          );
          // Add hint below the say block
          if (sayHint) {
            elements.push(
              <HintAlert key={`${idx}-hint`} text={sayHint} />
            );
          }
          break;
        }

        case 'tip': {
          const { text: tipText, hint: tipHint } = extractHint(element.text);
          elements.push(
            <TipBlock
              key={idx}
              text={parseMergeTags(tipText)}
              icon={element.icon}
            />
          );
          // Add hint below the tip block
          if (tipHint) {
            elements.push(
              <HintAlert key={`${idx}-hint`} text={tipHint} />
            );
          }
          break;
        }

        case 'textField':
          elements.push(
            <FormRow key={idx}>
              <ScriptTextField
                label={parseMergeTags(element.label)}
                required={element.required}
                placeholder={element.placeholder}
                value={formData[element.field] || ''}
                onChange={(e) => handleFormChange(element.field, e.target.value)}
                width={element.width}
              />
            </FormRow>
          );
          break;

        case 'select': {
          // For lead_source_type field, use Redux sourceType as fallback when API choices are empty
          const selectOptions = (element.field === 'leadType' && (!element.options || element.options.length === 0))
            ? (sourceType || [])
            : (element.options || []);
          elements.push(
            <FormRow key={idx}>
              <ScriptSelect
                label={parseMergeTags(element.label)}
                required={element.required}
                placeholder={element.placeholder}
                options={selectOptions}
                value={formData[element.field] || ''}
                onChange={(e) => handleFormChange(element.field, e.target.value)}
                width={element.width}
                menuMaxHeight={element.field === 'leadType' ? 250 : undefined}
              />
            </FormRow>
          );
          break;
        }

        case 'textarea':
          elements.push(
            <FormRow key={idx}>
              <ScriptTextarea
                label={parseMergeTags(element.label)}
                required={element.required}
                placeholder={element.placeholder}
                rows={element.rows}
                value={formData[element.field] || ''}
                onChange={(e) => handleFormChange(element.field, e.target.value)}
              />
            </FormRow>
          );
          break;

        case 'date':
          elements.push(
            <FormRow key={idx}>
              <ScriptDateField
                label={parseMergeTags(element.label)}
                required={element.required}
                value={formData[element.field] || ''}
                onChange={(e) => handleFormChange(element.field, e.target.value)}
                width={element.width}
              />
            </FormRow>
          );
          break;

        case 'formRow':
          elements.push(
            <FormRow key={idx}>
              {element.fields?.map((field, fIdx) => {
                if (field.type === 'text') {
                  return (
                    <ScriptTextField
                      key={fIdx}
                      label={parseMergeTags(field.label)}
                      required={field.required}
                      placeholder={field.placeholder}
                      value={formData[field.field] || ''}
                      onChange={(e) => handleFormChange(field.field, e.target.value)}
                      width={field.width}
                    />
                  );
                }
                if (field.type === 'select') {
                  return (
                    <ScriptSelect
                      key={fIdx}
                      label={parseMergeTags(field.label)}
                      required={field.required}
                      placeholder={field.placeholder}
                      options={field.options}
                      value={formData[field.field] || ''}
                      onChange={(e) => handleFormChange(field.field, e.target.value)}
                      width={field.width}
                    />
                  );
                }
                if (field.type === 'date') {
                  return (
                    <ScriptDateField
                      key={fIdx}
                      label={parseMergeTags(field.label)}
                      required={field.required}
                      value={formData[field.field] || ''}
                      onChange={(e) => handleFormChange(field.field, e.target.value)}
                      width={field.width}
                    />
                  );
                }
                return null;
              })}
            </FormRow>
          );
          break;

        case 'choiceGrid': {
          // Check if this is a HOT/WARM/COLD interest level selection
          const optionValues = element.options?.map((opt) =>
            (typeof opt === 'string' ? opt : opt.value)?.toLowerCase()
          ) || [];
          // Check if options contain hot, warm, cold (either exact match or starts with)
          const hasHot = optionValues.some((v) => v === 'hot' || v?.startsWith('hot ') || v?.startsWith('hot–') || v?.startsWith('hot-'));
          const hasWarm = optionValues.some((v) => v === 'warm' || v?.startsWith('warm ') || v?.startsWith('warm–') || v?.startsWith('warm-'));
          const hasCold = optionValues.some((v) => v === 'cold' || v?.startsWith('cold ') || v?.startsWith('cold–') || v?.startsWith('cold-'));
          const isInterestLevel = hasHot && hasWarm && hasCold;

          if (isInterestLevel && element.choiceType === 'radio') {
            // Use InterestCards for HOT/WARM/COLD selection
            const interestOptions = element.options.map((opt) => {
              const value = typeof opt === 'string' ? opt : opt.value;
              const label = typeof opt === 'string' ? opt : opt.label;
              const valueLower = value?.toLowerCase() || '';

              // Extract the interest level key (hot, warm, cold) from the value
              // Handle cases like "HOT – Description" or "hot" or "HOT-Description"
              let interestKey = 'default';
              if (valueLower === 'hot' || valueLower.startsWith('hot ') || valueLower.startsWith('hot–') || valueLower.startsWith('hot-')) {
                interestKey = 'hot';
              } else if (valueLower === 'warm' || valueLower.startsWith('warm ') || valueLower.startsWith('warm–') || valueLower.startsWith('warm-')) {
                interestKey = 'warm';
              } else if (valueLower === 'cold' || valueLower.startsWith('cold ') || valueLower.startsWith('cold–') || valueLower.startsWith('cold-')) {
                interestKey = 'cold';
              }

              // Default descriptions and emojis for interest levels
              const defaults = {
                hot: {
                  emoji: '🔥',
                  description: 'Highly interested, likely to book or proceed with treatment',
                  variant: 'hot',
                },
                warm: {
                  emoji: '☀️',
                  description: 'Some interest, may need more persuasion',
                  variant: 'warm',
                },
                cold: {
                  emoji: '❄️',
                  description: 'Not ready or not serious',
                  variant: 'cold',
                },
              };

              // Extract label from value (e.g., "HOT – Description" -> "HOT")
              const extractedLabel = value?.split(/[–-]/)?.[0]?.trim() || value;

              return {
                value,
                label: extractedLabel?.toUpperCase() || label?.toUpperCase() || value?.toUpperCase(),
                emoji: opt.emoji || defaults[interestKey]?.emoji || '',
                description: opt.description || defaults[interestKey]?.description || '',
                variant: defaults[interestKey]?.variant || 'default',
              };
            });

            elements.push(
              <Box key={idx}>
                <InterestCards
                  options={interestOptions}
                  selectedValue={formData[element.field]?.[0] || formData[element.field]}
                  onChange={(value) => handleFormChange(element.field, [value])}
                />
                {/* Follow-Up System Tip */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: '10px', sm: '12px' },
                    padding: { xs: '12px 14px', sm: '14px 16px' },
                    bgcolor: 'rgba(255,171,0,0.08)',
                    borderRadius: '8px',
                    mt: { xs: '16px', sm: '20px' },
                  }}
                >
                  <Iconify
                    icon="mdi:information-outline"
                    width={20}
                    height={20}
                    sx={{ color: '#B76E00', flexShrink: 0, mt: '2px' }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: '12px', sm: '13px' },
                      color: '#B76E00',
                      lineHeight: 1.5,
                    }}
                  >
                    <strong>Follow-Up System:</strong> Ensure WARM and COLD leads are tagged for proactive follow-up within specific timeframes (24 hours, 1 week, etc.) with email/SMS nudges linking testimonials or offers. Enter on CRM.
                  </Typography>
                </Box>
              </Box>
            );
          } else {
            elements.push(
              <ChoiceGrid
                key={idx}
                options={element.options}
                selectedValues={formData[element.field] || []}
                onChange={(values) => handleFormChange(element.field, values)}
                type={element.choiceType || 'checkbox'}
                single={element.single}
                name={element.field}
              />
            );
          }
          break;
        }

        case 'responseButtons':
          elements.push(
            <ResponseButtons
              key={idx}
              options={element.options?.map((opt) => ({
                ...opt,
                icon: getIconComponent(opt.iconName),
              }))}
              selectedValue={formData[element.field]}
              onChange={(value) => handleFormChange(element.field, value)}
              vertical={element.vertical}
            />
          );
          break;

        case 'scaleRating':
          elements.push(
            <ScaleRating
              key={idx}
              min={element.min || 1}
              max={element.max || 10}
              value={formData[element.field]}
              onChange={(value) => handleFormChange(element.field, value)}
            />
          );
          break;

        case 'sectionDivider':
          elements.push(
            <SectionDivider
              key={idx}
              title={parseMergeTags(element.title)}
              subtitle={parseMergeTags(element.subtitle)}
            />
          );
          break;

        case 'interestCards':
          elements.push(
            <InterestCards
              key={idx}
              options={element.options}
              selectedValue={formData[element.field]}
              onChange={(value) => handleFormChange(element.field, value)}
            />
          );
          break;

        case 'outcomeButtons':
          elements.push(
            <OutcomeButtons
              key={idx}
              options={element.options}
              selectedValue={formData[element.field]}
              onChange={handleOutcomeSelect}
            />
          );
          break;

        // Special field types
        case 'leadSelector':
        case 'lead_search_select':
          if (previewMode) {
            // In preview mode, show a placeholder lead selector
            elements.push(
              <FormRow key={idx}>
                <Box
                  sx={{
                    width: '100%',
                    p: 2,
                    borderRadius: '10px',
                    border: '1px dashed rgba(145, 158, 171, 0.32)',
                    bgcolor: 'rgba(145, 158, 171, 0.04)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'rgba(0, 167, 111, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Iconify icon="mdi:account-search" width={22} height={22} sx={{ color: '#00A76F' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#212B36' }}>
                        Lead Selection
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#637381' }}>
                        In live mode, a lead will be selected or searched here
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </FormRow>
            );
            // Show placeholder lead detail fields in preview mode
            elements.push(
              <FormRow key={`${idx}-lead-details`}>
                <ScriptSelect
                  label="Title"
                  placeholder="Select title"
                  options={CANONICAL_SALUTATION_VALUES}
                  value=""
                  onChange={() => {}}
                  width="25%"
                  disabled
                />
                <ScriptTextField
                  label="First Name"
                  placeholder="John"
                  value=""
                  onChange={() => {}}
                  width="37.5%"
                  disabled
                />
                <ScriptTextField
                  label="Last Name"
                  placeholder="Doe"
                  value=""
                  onChange={() => {}}
                  width="37.5%"
                  disabled
                />
              </FormRow>
            );
          } else {
            elements.push(
              <FormRow key={idx}>
                <LeadSearchField
                  label={parseMergeTags(element.label)}
                  value={formData.leadName || ''}
                  onChange={(value) => handleFormChange('leadName', value)}
                  onLeadSelect={handleLeadSelect}
                  required={element.required}
                  disabled={!!followLead?.first_name}
                  placeholder={element.placeholder || 'Search for a lead...'}
                />
              </FormRow>
            );
            // Show editable lead detail fields when a lead is selected
            if (formData.lead_id || followLead?.id) {
              elements.push(
                <FormRow key={`${idx}-lead-details`}>
                  <ScriptSelect
                    label="Title"
                    placeholder="Select title"
                    options={salutationOptions?.length ? salutationOptions.map((opt) => opt.value) : CANONICAL_SALUTATION_VALUES}
                    value={formData.title || ''}
                    onChange={(e) => handleFormChange('title', e.target.value)}
                    width="25%"
                  />
                  <ScriptTextField
                    label="First Name"
                    required
                    placeholder="First name"
                    value={formData.firstName || ''}
                    onChange={(e) => handleFormChange('firstName', e.target.value)}
                    width="37.5%"
                  />
                  <ScriptTextField
                    label="Last Name"
                    placeholder="Last name"
                    value={formData.lastName || ''}
                    onChange={(e) => handleFormChange('lastName', e.target.value)}
                    width="37.5%"
                  />
                </FormRow>
              );
            }
          }
          break;

        case 'treatmentSelector':
        case 'practice_treatment_search_select':
          if (previewMode) {
            // In preview mode, show a placeholder treatment selector
            elements.push(
              <FormRow key={idx}>
                <Box
                  sx={{
                    width: '100%',
                    p: 2,
                    borderRadius: '10px',
                    border: '1px dashed rgba(145, 158, 171, 0.32)',
                    bgcolor: 'rgba(145, 158, 171, 0.04)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'rgba(0, 184, 217, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Iconify icon="mdi:tooth" width={22} height={22} sx={{ color: '#00B8D9' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#212B36' }}>
                        Treatment Selection
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#637381' }}>
                        Treatment, price, and teeth count will appear here
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '6px',
                        bgcolor: 'rgba(145, 158, 171, 0.16)',
                        fontSize: '0.75rem',
                        color: '#637381',
                      }}
                    >
                      £0.00
                    </Box>
                  </Box>
                </Box>
              </FormRow>
            );
          } else {
            elements.push(
              <FormRow key={idx}>
                <Box sx={{ width: '100%' }} ref={treatmentButtonRef}>
                  {treatmentPopoverOpen && (
                    <TreatmentEditPopover
                      treatment={formData.treatment || ''}
                      leadTreatmentSpecification={{ teeth_count: formData.teethAmount }}
                      open={treatmentPopoverOpen}
                      anchorEl={treatmentAnchorEl}
                      handleClickAwayLister={handleTreatmentClose}
                      treatmentPrice={formData.treatmentValue}
                      handleSaveTreatment={handleSaveTreatment}
                      isFlow
                      isScript
                    />
                  )}
                  <TreatmentFlowComponent
                    treatmentData={formData.treatment}
                    teethAmount={formData.teethAmount}
                    handleTreatment={handleTreatmentClick}
                    amount={formData.treatmentValue}
                    amountError={false}
                    amountHandleChange={handleTreatmentAmountChange}
                    handleChangeTeeth={handleTeethChange}
                    teethAmountError={false}
                  />
                </Box>
              </FormRow>
            );
          }
          break;

        case 'assigneeSelector':
        case 'practice_user_search_select': {
          if (previewMode) {
            // In preview mode, show a placeholder assignee selector
            elements.push(
              <FormRow key={idx}>
                <Box
                  sx={{
                    width: '100%',
                    p: 2,
                    borderRadius: '10px',
                    border: '1px dashed rgba(145, 158, 171, 0.32)',
                    bgcolor: 'rgba(145, 158, 171, 0.04)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255, 171, 0, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Iconify icon="mdi:account-group" width={22} height={22} sx={{ color: '#FFAB00' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#212B36' }}>
                        User Assignment
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#637381' }}>
                        Team member will be assigned here
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </FormRow>
            );
            break;
          }

          const selectedUsers = assignToState || [];
          const hasUsers = selectedUsers.length > 0;
          const isLoading = !practiceUser?.results && practiceId;

          // Helper to get user display info
          const getUserInfo = (user) => {
            const name = user?.user
              ? `${user.user.first_name || ''} ${user.user.last_name || ''}`.trim()
              : '';
            const initials = user?.user
              ? `${user.user.first_name?.[0] || ''}${user.user.last_name?.[0] || ''}`.toUpperCase()
              : '';
            return { name, initials, avatar: user?.user?.avatar || '' };
          };

          // Render assignee content based on state
          const renderAssigneeContent = () => {
            if (isLoading) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Skeleton variant="circular" width={36} height={36} />
                  <Skeleton variant="text" width={120} height={24} />
                </Box>
              );
            }

            if (hasUsers) {
              // Get display name - show first user's name, or count if multiple
              const displayName = selectedUsers.length === 1
                ? getUserInfo(selectedUsers[0]).name || 'Unknown User'
                : `${selectedUsers.length} users selected`;

              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1,
                    borderRadius: '10px',
                    bgcolor: 'rgba(145, 158, 171, 0.08)',
                    border: '1px solid rgba(145, 158, 171, 0.16)',
                  }}
                >
                  {/* Avatar stack - show up to 2 avatars */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {selectedUsers.slice(0, 2).map((user, index) => {
                      const { initials, avatar } = getUserInfo(user);
                      return (
                        <Avatar
                          key={user.id || index}
                          src={avatar}
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: index === 0 ? '#00A76F' : '#637381',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            marginLeft: index > 0 ? '-10px' : 0,
                            border: '2px solid #fff',
                            zIndex: 2 - index,
                          }}
                        >
                          {initials}
                        </Avatar>
                      );
                    })}
                    {selectedUsers.length > 2 && (
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          bgcolor: '#919EAB',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginLeft: '-10px',
                          border: '2px solid #fff',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#fff',
                        }}
                      >
                        +{selectedUsers.length - 2}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: '#212B36',
                        lineHeight: 1.4,
                      }}
                    >
                      {displayName}
                    </Typography>
                    {userAutoSelected && selectedUsers.length === 1 && (
                      <Typography
                        sx={{
                          fontSize: '0.6875rem',
                          color: '#00A76F',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Iconify icon="mdi:check-circle" width={12} height={12} />
                        Auto-selected
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={handleAssigneePopupOpen}
                    sx={{
                      color: '#637381',
                      '&:hover': { bgcolor: 'rgba(0, 167, 111, 0.08)', color: '#00A76F' },
                    }}
                  >
                    <Iconify icon="mdi:pencil" width={18} height={18} />
                  </IconButton>
                </Box>
              );
            }

            return (
              <Box
                onClick={handleAssigneePopupOpen}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1,
                  borderRadius: '10px',
                  border: '1px dashed rgba(145, 158, 171, 0.32)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#00A76F',
                    bgcolor: 'rgba(0, 167, 111, 0.04)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(145, 158, 171, 0.16)',
                  }}
                >
                  <Iconify icon="mdi:plus" width={20} height={20} sx={{ color: '#637381' }} />
                </Box>
                <Typography sx={{ fontSize: '0.875rem', color: '#637381' }}>
                  Select user
                </Typography>
              </Box>
            );
          };

          elements.push(
            <FormRow key={idx}>
              <Box sx={{ width: '100%' }}>
                {renderAssigneeContent()}

                {/* Assignee Selection Popup */}
                {assigneePopupOpen && (
                  <ClickAwayListener onClickAway={handleAssigneePopupClose}>
                    <Popper
                      open={assigneePopupOpen}
                      anchorEl={assigneeAnchorEl}
                      transition
                      placement="top-end"
                      sx={{ zIndex: 1300 }}
                      modifiers={[
                        {
                          name: 'flip',
                          enabled: true,
                          options: {
                            fallbackPlacements: ['top-start', 'bottom-end', 'bottom-start'],
                          },
                        },
                        {
                          name: 'preventOverflow',
                          enabled: true,
                          options: {
                            boundary: 'viewport',
                            padding: 16,
                          },
                        },
                        {
                          name: 'offset',
                          options: {
                            offset: [0, 8],
                          },
                        },
                      ]}
                    >
                      {({ TransitionProps }) => (
                        <Grow {...TransitionProps}>
                          <Paper sx={{ borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: '60vh', overflow: 'auto' }}>
                            <AssignTaskPopup
                              open={assigneePopupOpen}
                              onClose={handleAssigneePopupClose}
                              type="AddReminder"
                              setAssignTo={handleAssigneeChange}
                              assignees={assignToState?.map((ele) => ele.id)}
                            />
                          </Paper>
                        </Grow>
                      )}
                    </Popper>
                  </ClickAwayListener>
                )}
              </Box>
            </FormRow>
          );
          break;
        }

        // Special attribute-based UI overrides
        case 'capture_contact_details':
          elements.push(
            <FormRow key={idx}>
              <CaptureContactDetailsLive
                formData={formData}
                onChange={handleFormChange}
                referralOptions={referralOptions}
                disabled={previewMode}
              />
            </FormRow>
          );
          break;

        case 'confirm_speaker':
          elements.push(
            <FormRow key={idx}>
              <ConfirmSpeakerLive
                formData={formData}
                onChange={handleFormChange}
                disabled={previewMode}
                onSelect={handleConfirmSpeakerSelect}
              />
            </FormRow>
          );
          break;

        case 'answer_the_call':
          // CompletedBySelector is shown in card header (headerRight), no content needed here
          break;

        case 'identify_caller':
          if (previewMode) {
            // In preview mode, show the static placeholder
            elements.push(
              <FormRow key={idx}>
                <IdentifyCaller label={element.label} required={element.required} />
              </FormRow>
            );
          } else {
            // In live mode, show the interactive component
            elements.push(
              <FormRow key={idx}>
                <IdentifyCallerLive
                  ref={identifyCallerRef}
                  formData={formData}
                  onChange={handleFormChange}
                  practiceId={practiceId}
                  scriptCategory={scriptCategory}
                  disabled={previewMode}
                  onLeadCreated={(lead) => {
                    // Populate form with new lead data (minimal data since just created)
                    handleLeadSelect(lead);
                  }}
                  onLeadSelected={(lead) => {
                    // Populate form with selected lead's contact info
                    handleLeadSelect(lead);
                  }}
                />
              </FormRow>
            );
          }
          break;

        case 'reason_for_calling':
          if (previewMode) {
            // In preview mode, show the static placeholder
            elements.push(
              <FormRow key={idx}>
                <ReasonForCalling label={element.label} />
              </FormRow>
            );
          } else {
            // In live mode, show the interactive component
            elements.push(
              <FormRow key={idx}>
                <ReasonForCallingLive
                  formData={formData}
                  onChange={handleFormChange}
                  onTreatmentSelect={handleSaveTreatment}
                  reasonOptions={reasonForCallingOptions}
                  disabled={previewMode}
                  required={element.required}
                />
              </FormRow>
            );
          }
          break;

        case 'close_call':
          if (previewMode) {
            // In preview mode, show the static placeholder
            elements.push(
              <FormRow key={idx}>
                <CloseCall label={element.label} required={element.required} metadata={card.metadata} />
              </FormRow>
            );
          } else {
            // In live mode, show the interactive component
            elements.push(
              <FormRow key={idx}>
                <CloseCallLive
                  formData={formData}
                  onChange={handleFormChange}
                  isAiEnabled={isAiCallSummaryEnabled}
                  aiSummaryLoading={aiSummaryLoading}
                  onGenerateAISummary={handleGenerateAISummary}
                  onOutcomeSelect={handleOutcomeSelect}
                  disabled={previewMode}
                  previewMode={previewMode}
                  metadata={card.metadata}
                />
              </FormRow>
            );
          }
          break;

        default:
          break;
      }
    });

    // Render hint blocks below card content (empathy, sales, etc. from metadata.hints).
    // Strip toggled-off types (sales / empathy) before passing in so HintBlocks renders
    // nothing when no remaining types exist (avoids an empty divider box).
    if (card.metadata?.hints) {
      const filteredHints = Object.fromEntries(
        Object.entries(card.metadata.hints).filter(
          ([key]) => !hiddenHintTypes.includes(key)
        )
      );
      elements.push(
        <HintBlocks key="meta-hints" hints={filteredHints} />
      );
    }

    // Add note toggle + optional Send email / Send WhatsApp buttons for each card.
    // In scrollable mode, cardIndexOverride is passed; in card mode, use currentCardIndex.
    const noteCardIdx = cardIndexOverride != null ? cardIndexOverride : currentCardIndex;
    // Sibling action buttons reuse noteToggleSx(false) for a unified look, overriding
    // width so the full-width rule doesn't apply to these shorter pill-style buttons.
    const actionBtnSx = { ...noteToggleSx(false), width: 'auto' };
    elements.push(
      <Box key="note-and-actions" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
        <NoteToggle
          value={cardNotes[noteCardIdx] || ''}
          onChange={cardIndexOverride != null
            ? (note) => handleNoteChangeForCard(noteCardIdx, note)
            : handleNoteChange
          }
        />
        {showQuestionActionIcons && (
          <>
            <Box onClick={() => setEmailComposerOpen(true)} sx={actionBtnSx}>
              <Iconify icon="eva:email-fill" width={18} height={18} />
              <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 500 }}>Send email</Typography>
            </Box>
            <Box onClick={() => setWhatsappComposerOpen(true)} sx={actionBtnSx}>
              <Iconify icon="mdi:whatsapp" width={18} height={18} />
              <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 500 }}>Send WhatsApp</Typography>
            </Box>
          </>
        )}
      </Box>
    );

    return elements;
  };

  // Helper to get icon component by name using Iconify
  const getIconComponent = (iconName) => {
    const iconMap = {
      PersonAdd: 'mdi:account-plus',
      Person: 'mdi:account',
      Check: 'mdi:check',
      Close: 'mdi:close',
      Schedule: 'mdi:clock-outline',
      ScheduleSend: 'mdi:clock-check-outline',
      CheckCircle: 'mdi:check-circle',
      PhoneMissed: 'mdi:phone-missed',
      PersonOff: 'mdi:account-off',
      LocationOn: 'mdi:map-marker',
      DirectionsCar: 'mdi:car',
    };
    const iconKey = iconMap[iconName];
    return iconKey ? <Iconify icon={iconKey} width={20} height={20} /> : null;
  };

  // Check if current card was skipped
  const wasSkipped = skippedCards.has(currentCardIndex);

  // Check if current card has required fields
  const currentCardHasRequiredFields = useMemo(() => {
    if (!currentCard?.elements) return false;
    return currentCard.elements.some((el) => {
      if (el.required) return true;
      // Check formRow fields
      if (el.type === 'formRow' && el.fields) {
        return el.fields.some((f) => f.required);
      }
      return false;
    });
  }, [currentCard]);

  // Questions with notes for current section (use pre-computed global indices)
  const questionsWithNotes = currentSectionQuestions
    .map((_, idx) => {
      const cardIdx = sectionCardGlobalIndices[idx];
      return cardNotes[cardIdx] ? idx : null;
    })
    .filter((idx) => idx !== null);

  // Completed/skipped questions for current section
  const completedQuestionsInSection = currentSectionQuestions
    .map((_, idx) => {
      const cardIdx = sectionCardGlobalIndices[idx];
      return completedCards.has(cardIdx) ? idx : null;
    })
    .filter((idx) => idx !== null);

  const skippedQuestionsInSection = currentSectionQuestions
    .map((_, idx) => {
      const cardIdx = sectionCardGlobalIndices[idx];
      return skippedCards.has(cardIdx) ? idx : null;
    })
    .filter((idx) => idx !== null);

  const autoFilledQuestionsInSection = currentSectionQuestions
    .map((_, idx) => {
      const cardIdx = sectionCardGlobalIndices[idx];
      return autoFilledCards.has(cardIdx) && !completedCards.has(cardIdx) ? idx : null;
    })
    .filter((idx) => idx !== null);

  // Questions in current section that have conditional logic attached
  // (either new metadata.conditionLogic OR legacy form-level conditional_logics[] entry).
  // Only surfaced in preview / JSON edit mode so editors can see which questions branch.
  const conditionalQuestionsInSection = previewMode
    ? currentSectionQuestions
        .map((card, idx) => {
          const hasNewLogic = Array.isArray(card?.metadata?.conditionLogic?.conditions)
            && card.metadata.conditionLogic.conditions.length > 0;
          const hasLegacyLogic = Array.isArray(conditionalLogics)
            && conditionalLogics.some(
              (cl) => cl?.target_section === card?.originalSectionId
                && cl?.target_field === card?.fieldId
            );
          return (hasNewLogic || hasLegacyLogic) ? idx : null;
        })
        .filter((idx) => idx !== null)
    : [];

  // Gate flag — show the inline Send email / Send WhatsApp buttons whenever this is
  // not the editor preview surface. Composers handle empty lead context themselves
  // (agent can fill recipient manually if no lead has been identified yet).
  const showQuestionActionIcons = !previewMode;

  // Hint visibility toggle chips, rendered next to the Save button in the sticky footer.
  // Three chips — Coaching, Sales, Empathy — each a small MUI Chip in filled (on) /
  // outlined+muted (off) states. Available on both live-call and editor preview surfaces
  // so editors see what an agent will see. Persists per-user via localStorage.
  // Memoized: rebuilt only when hiddenHintTypes or toggleHintType changes.
  const hintToggleChips = useMemo(() => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        ml: 1,
        flexWrap: 'wrap',
      }}
      aria-label="Hint visibility toggles"
    >
      {HINT_TOGGLE_TYPES.map((type) => {
        const hidden = hiddenHintTypes.includes(type);
        const label = type.charAt(0).toUpperCase() + type.slice(1);
        return (
          <Tooltip
            key={type}
            title={hidden ? `Show ${type} hints` : `Hide ${type} hints`}
            arrow
            placement="top"
          >
            <Chip
              size="small"
              label={label}
              onClick={() => toggleHintType(type)}
              icon={
                <Iconify
                  icon={hidden ? 'mdi:eye-off-outline' : 'mdi:eye-outline'}
                  width={14}
                  height={14}
                />
              }
              variant={hidden ? 'outlined' : 'filled'}
              sx={{
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '0.75rem',
                height: 28,
                color: hidden ? 'text.disabled' : 'text.primary',
                bgcolor: hidden ? 'transparent' : 'action.selected',
                borderColor: hidden ? 'divider' : 'transparent',
                textDecoration: hidden ? 'line-through' : 'none',
                '& .MuiChip-icon': {
                  color: 'inherit',
                  ml: '6px',
                },
                '&:hover': {
                  bgcolor: hidden ? 'action.hover' : 'action.focus',
                },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  ), [hiddenHintTypes, toggleHintType]);

  return (
    <Box sx={appContainerSx}>
      <CallScriptHeader
        title={effectiveScriptData.title || 'Call Script'}
        subtitle={effectiveScriptData.subtitle}
        type={type}
        scriptCategory={scriptCategory}
        currentStep={currentStep}
        totalSteps={totalSteps}
        saveStatus={saveStatus}
        onClose={onClose}
        onToggleContext={() => setContextPanelOpen(!contextPanelOpen)}
        showContextToggle
        draftSlot={
          <DraftRecoveryBanner
            practiceId={practiceId}
            scriptCategory={scriptCategory}
            currentDraftId={draftIdRef.current}
            onRestore={handleRestoreDraft}
            previewMode={previewMode}
          />
        }
      />

      <Box sx={mainContainerSx}>
        <Box sx={scriptPanelSx}>
          {isScriptSwitching ? (
            <Fade in timeout={300}>
              <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Section tabs skeleton */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Skeleton variant="rounded" width={90} height={32} sx={{ borderRadius: '16px' }} />
                  <Skeleton variant="rounded" width={110} height={32} sx={{ borderRadius: '16px' }} />
                  <Skeleton variant="rounded" width={80} height={32} sx={{ borderRadius: '16px' }} />
                </Box>
                {/* Question progress skeleton */}
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} variant="circular" width={24} height={24} />
                  ))}
                </Box>
                {/* Card content skeleton */}
                <Box sx={{ mt: 1 }}>
                  <Skeleton variant="rounded" width="35%" height={22} sx={{ mb: 1.5, borderRadius: '6px' }} />
                  <Skeleton variant="rounded" width="70%" height={28} sx={{ mb: 1, borderRadius: '8px' }} />
                  <Skeleton variant="rounded" width="50%" height={18} sx={{ mb: 3, borderRadius: '6px' }} />
                  <Skeleton variant="rounded" width="100%" height={48} sx={{ mb: 2, borderRadius: '10px' }} />
                  <Skeleton variant="rounded" width="100%" height={48} sx={{ mb: 2, borderRadius: '10px' }} />
                  <Skeleton variant="rounded" width="60%" height={48} sx={{ borderRadius: '10px' }} />
                </Box>
              </Box>
            </Fade>
          ) : (
            <Fade in timeout={400}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <SectionTabs
                  sections={sections}
                  currentSectionId={currentSectionId}
                  completedSections={completedSections}
                  sectionsWithSkipped={sectionsWithSkipped}
                  onSectionClick={handleSectionClick}
                />

                {/* Simulating-treatment banner: editor-only, purple to match the conditional-logic accent */}
                {isSimulating && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mx: 2,
                      mt: 1,
                      mb: 0.5,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: '8px',
                      bgcolor: 'rgba(118,53,220,0.08)',
                      border: '1px solid rgba(118,53,220,0.24)',
                    }}
                  >
                    <Iconify
                      icon="mdi:source-branch"
                      width={16}
                      height={16}
                      sx={{ color: '#7635DC', flexShrink: 0 }}
                    />
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#431A9E',
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Simulating: {simulatedTreatment.name}
                      {simulatedTreatment.category ? ` · ${simulatedTreatment.category}` : ''}
                    </Typography>
                    {onClearSimulatedTreatment && (
                      <IconButton
                        size="small"
                        onClick={onClearSimulatedTreatment}
                        aria-label="Clear simulated treatment"
                        sx={{
                          width: 22,
                          height: 22,
                          color: '#7635DC',
                          '&:hover': { bgcolor: 'rgba(118,53,220,0.16)' },
                        }}
                      >
                        <Iconify icon="mdi:close" width={14} height={14} />
                      </IconButton>
                    )}
                  </Box>
                )}

                {/* Hide QuestionProgress on closure section (no questions) */}
                {!isOnClosureSection && (
                  <QuestionProgress
                    questions={currentSectionQuestions}
                    currentQuestionIndex={currentQuestionInSection}
                    completedQuestions={completedQuestionsInSection}
                    skippedQuestions={skippedQuestionsInSection}
                    autoFilledQuestions={autoFilledQuestionsInSection}
                    questionsWithNotes={questionsWithNotes}
                    conditionalQuestions={conditionalQuestionsInSection}
                    onQuestionClick={handleQuestionClick}
                  />
                )}

                <Box ref={scrollableAreaRef} sx={scrollableView ? scrollableCardAreaSx : cardAreaSx}>
                  {/* Warning banner when user is past section 1 but has no lead selected */}
                  {!previewMode && currentSectionId > 1 && !formData.selectedLeadId && !formData.lead_id && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 2,
                        mb: 2,
                        borderRadius: '12px',
                        bgcolor: 'rgba(255, 86, 48, 0.08)',
                        border: '1px solid rgba(255, 86, 48, 0.24)',
                      }}
                    >
                      <Iconify
                        icon="mdi:alert-circle"
                        width={24}
                        height={24}
                        sx={{ color: '#FF5630', flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: '#B71D18',
                            mb: 0.25,
                          }}
                        >
                          No lead selected
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.8125rem',
                            color: '#FF5630',
                          }}
                        >
                          Please go back to Section 1 and search for or create a lead to continue.
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          // Find the identify_caller card and navigate to it
                          const identifyCallerCardIndex = cards.findIndex(
                            (c) => c.elements?.some((el) => el.type === 'identify_caller')
                          );
                          if (identifyCallerCardIndex !== -1) {
                            setCurrentCardIndex(identifyCallerCardIndex);
                          } else {
                            // Fallback to first card
                            setCurrentCardIndex(0);
                          }
                        }}
                        sx={{
                          borderColor: '#FF5630',
                          color: '#FF5630',
                          '&:hover': {
                            borderColor: '#B71D18',
                            bgcolor: 'rgba(255, 86, 48, 0.08)',
                          },
                        }}
                      >
                        Go to Lead Selection
                      </Button>
                    </Box>
                  )}

                  {/* === SCROLLABLE VIEW: Render all cards in current section === */}
                  {/* Performance: only the active card renders full content; collapsed cards render header only */}
                  {scrollableView && !isOnClosureSection && currentSectionQuestions.map((card, sectionIdx) => {
                    const globalIdx = sectionCardGlobalIndices[sectionIdx];
                    const isActiveCard = globalIdx === currentCardIndex;
                    const cardSkipped = skippedCards.has(globalIdx);
                    const cardCompleted = completedCards.has(globalIdx);
                    const cardHasRequired = card.elements?.some((el) => {
                      if (el.required) return true;
                      if (el.type === 'formRow' && el.fields) return el.fields.some((f) => f.required);
                      return false;
                    });

                    // All cards render fully expanded — active card gets highlight styling
                    const hasConfirmSpeaker = card?.elements?.some(el => el.type === 'confirm_speaker');
                    const hasAnswerTheCall = card?.elements?.some(el => el.type === 'answer_the_call');
                    const showCompletedBy = hasConfirmSpeaker || hasAnswerTheCall;
                    const completedByField = hasAnswerTheCall ? 'answerTheCallCompletedBy' : 'completedBy';
                    const completedByValue = hasAnswerTheCall ? formData.answerTheCallCompletedBy : formData.completedBy;

                    const hasConditionLogic = card.metadata?.conditionLogic;
                    const conditionLogicText = hasConditionLogic
                      ? formatConditionLogicForPreview(card.metadata.conditionLogic)
                      : '';

                    return (
                      <ScriptCard
                        key={card.id || globalIdx}
                        ref={(el) => { scrollableCardRefs.current[globalIdx] = el; }}
                        badge={card.badge}
                        badgeClass={card.badgeClass}
                        title={parseMergeTags(card.title)}
                        subtitle={parseMergeTags(card.metadata?.subLabel || card.subtitle)}
                        wasSkipped={cardSkipped}
                        hasRequiredFields={cardHasRequired}
                        isScrollableView
                        isActive={isActiveCard}
                        isCompleted={cardCompleted}
                        stepNumber={sectionIdx + 1}
                        onClick={() => {
                          setCurrentCardIndex(globalIdx);
                          if (!isActiveCard) {
                            setTimeout(() => {
                              const ref = scrollableCardRefs.current[globalIdx];
                              if (ref) ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }, 50);
                          }
                        }}
                        onUnskip={() => {
                          setSkippedCards((prev) => {
                            const newSet = new Set(prev);
                            newSet.delete(globalIdx);
                            return newSet;
                          });
                          if (!isActiveCard) setCurrentCardIndex(globalIdx);
                        }}
                        headerRight={showCompletedBy ? (
                          <CompletedBySelector
                            users={previewMode ? [{ id: 1, user: { first_name: 'Current', last_name: 'User' } }] : (practiceUser?.results || [])}
                            selectedUserId={previewMode ? 1 : completedByValue}
                            onChange={(userId) => handleFormChange(completedByField, userId)}
                            disabled={previewMode}
                          />
                        ) : null}
                      >
                        {previewMode && hasConditionLogic && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.5,
                              p: 1.5,
                              mb: 2,
                              borderRadius: '10px',
                              bgcolor: 'rgba(142, 51, 255, 0.08)',
                              border: '1px solid rgba(142, 51, 255, 0.24)',
                            }}
                          >
                            <Iconify
                              icon="mdi:source-branch"
                              width={20}
                              height={20}
                              sx={{ color: '#8E33FF', flexShrink: 0, mt: '2px' }}
                            />
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: '0.8125rem',
                                  fontWeight: 600,
                                  color: '#5119B7',
                                  mb: 0.5,
                                }}
                              >
                                Conditional Field
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  color: '#7635DC',
                                  lineHeight: 1.5,
                                }}
                              >
                                {conditionLogicText}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        {renderCardContent(card, globalIdx)}
                      </ScriptCard>
                    );
                  })}

                  {/* === CARD VIEW (default): Render single current card === */}
                  {!scrollableView && currentCard && (() => {
                    const hasConfirmSpeaker = currentCard?.elements?.some(el => el.type === 'confirm_speaker');
                    const hasAnswerTheCall = currentCard?.elements?.some(el => el.type === 'answer_the_call');
                    const showCompletedBy = hasConfirmSpeaker || hasAnswerTheCall;
                    const completedByField = hasAnswerTheCall ? 'answerTheCallCompletedBy' : 'completedBy';
                    const completedByValue = hasAnswerTheCall ? formData.answerTheCallCompletedBy : formData.completedBy;

                    // Check if card has conditional logic (for preview mode indicator)
                    const hasConditionLogic = currentCard.metadata?.conditionLogic;
                    const conditionLogicText = hasConditionLogic
                      ? formatConditionLogicForPreview(currentCard.metadata.conditionLogic)
                      : '';

                    return (
              <ScriptCard
                badge={currentCard.badge}
                badgeClass={currentCard.badgeClass}
                title={parseMergeTags(currentCard.title)}
                subtitle={parseMergeTags(currentCard.metadata?.subLabel || currentCard.subtitle)}
                wasSkipped={wasSkipped}
                hasRequiredFields={currentCardHasRequiredFields}
                onUnskip={() => {
                  setSkippedCards((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(currentCardIndex);
                    return newSet;
                  });
                }}
                headerRight={showCompletedBy ? (
                  <CompletedBySelector
                    users={previewMode ? [{ id: 1, user: { first_name: 'Current', last_name: 'User' } }] : (practiceUser?.results || [])}
                    selectedUserId={previewMode ? 1 : completedByValue}
                    onChange={(userId) => handleFormChange(completedByField, userId)}
                    disabled={previewMode}
                  />
                ) : null}
              >
                {/* Conditional Logic Indicator for Preview Mode */}
                {previewMode && hasConditionLogic && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                      mb: 2,
                      borderRadius: '10px',
                      bgcolor: 'rgba(142, 51, 255, 0.08)',
                      border: '1px solid rgba(142, 51, 255, 0.24)',
                    }}
                  >
                    <Iconify
                      icon="mdi:source-branch"
                      width={20}
                      height={20}
                      sx={{ color: '#8E33FF', flexShrink: 0, mt: '2px' }}
                    />
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.8125rem',
                          fontWeight: 600,
                          color: '#5119B7',
                          mb: 0.5,
                        }}
                      >
                        Conditional Field
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#7635DC',
                          lineHeight: 1.5,
                        }}
                      >
                        {conditionLogicText}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {renderCardContent(currentCard)}
              </ScriptCard>
                    );
                  })()}

            {/* Closure Section - Separate mandatory section for call outcome */}
            {/* Closure Section - Only show if close_call attribute is NOT in script */}
            {isOnClosureSection && !hasCloseCallAttribute && (() => {
              // Determine outcome options based on script category
              // INBOUND/TCO: Only "Submit" button
              // OUTBOUND: 2 buttons (Not Booked, Booked)
              const isOutbound = scriptCategory?.toUpperCase() === 'OUTBOUND';

              const inboundTcoOptions = [
                {
                  value: 'submit',
                  label: 'Submit',
                  iconName: 'formkit:submit',
                  variant: 'booked',
                },
              ];

              const outboundOptions = [
                {
                  value: 'not-booked',
                  label: 'Appointment Not Booked',
                  iconName: 'ant-design:calendar-filled',
                  variant: 'not-booked',
                },
                {
                  value: 'booked',
                  label: 'Book an Appointment',
                  iconName: 'akar-icons:circle-check-fill',
                  variant: 'booked',
                },
              ];

              const outcomeOptions = isOutbound ? outboundOptions : inboundTcoOptions;

              // Determine subtitle based on mode and category
              const getSubtitle = () => {
                if (previewMode) return 'In live mode, this action will be available';
                if (isOutbound) return 'Select the outcome of this call to complete the script';
                return 'Submit the script to save all collected information';
              };

              return (
                <ScriptCard
                  badge={previewMode ? 'Preview' : 'Required'}
                  badgeClass={previewMode ? 'info' : 'required'}
                  title={isOutbound ? 'Call Outcome' : 'Complete Script'}
                  subtitle={getSubtitle()}
                >
                  <Box sx={{ py: 1 }}>
                    {previewMode ? (
                      // Preview mode - show disabled outcome buttons with info message
                      <Box>
                        <Box
                          sx={{
                            p: 2,
                            mb: 2,
                            borderRadius: '10px',
                            bgcolor: 'rgba(0, 184, 217, 0.08)',
                            border: '1px solid rgba(0, 184, 217, 0.24)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                          }}
                        >
                          <Iconify icon="mdi:information" width={20} height={20} sx={{ color: '#00B8D9' }} />
                          <Typography sx={{ fontSize: '0.875rem', color: '#006C9C' }}>
                            This is a preview. {isOutbound ? 'Outcome selection' : 'Submit action'} is available when using the script with a lead.
                          </Typography>
                        </Box>
                        <OutcomeButtons
                          options={outcomeOptions}
                          selectedValue={null}
                          onChange={() => {}}
                          disabled
                        />
                      </Box>
                    ) : (
                      // Live mode - show AI Summary first (if enabled), then outcome buttons
                      <>
                        {/* AI Summary Prompt - Primary CTA (only if enabled in AI Config) */}
                        <FeatureGuard feature="ai-call-summary" level="view">
                          {isAiCallSummaryEnabled && (
                            <>
                              <AISummaryPrompt
                                onGenerateSummary={handleGenerateAISummary}
                                isLoading={aiSummaryLoading}
                                disabled={false}
                              />

                              {/* Divider with "or" */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  my: 2,
                                }}
                              >
                                <Box sx={{ flex: 1, height: '1px', bgcolor: '#E5E7EB' }} />
                                <Typography
                                  sx={{
                                    fontSize: '0.75rem',
                                    color: '#9CA3AF',
                                    fontWeight: 500,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                  }}
                                >
                                  or continue without
                                </Typography>
                                <Box sx={{ flex: 1, height: '1px', bgcolor: '#E5E7EB' }} />
                              </Box>
                            </>
                          )}
                        </FeatureGuard>

                        {isOutbound && (
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#637381', mb: 1.5 }}>
                            How did the call end?
                          </Typography>
                        )}
                        <OutcomeButtons
                          options={outcomeOptions}
                          selectedValue={formData.callOutcome}
                          onChange={handleOutcomeSelect}
                        />
                      </>
                    )}
                  </Box>
                </ScriptCard>
              );
            })()}
          </Box>

          {/* Validation Errors - Compact inline notification */}
          {validationErrors.length > 0 && (
            <Box
              sx={{
                mx: 2,
                mb: 2,
                p: 1.5,
                borderRadius: '0.75rem',
                bgcolor: 'rgba(255, 86, 48, 0.08)',
                border: '1px solid rgba(255, 86, 48, 0.24)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Iconify icon="mdi:alert-circle" width={18} height={18} sx={{ color: '#FF5630' }} />
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#B71D18' }}>
                    {validationErrors.length} required field{validationErrors.length > 1 ? 's' : ''} missing
                  </Typography>
                </Box>
                <Box
                  component="button"
                  onClick={() => setValidationErrors([])}
                  sx={{
                    border: 'none',
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    p: 0.5,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': { bgcolor: 'rgba(255, 86, 48, 0.16)' },
                  }}
                >
                  <Iconify icon="mdi:close" width={16} height={16} sx={{ color: '#B71D18' }} />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {validationErrors.slice(0, 5).map((error, index) => (
                  <Box
                    key={`${error.fieldName}-${index}`}
                    component="button"
                    onClick={() => navigateToError(error)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: '1rem',
                      bgcolor: '#fff',
                      border: '1px solid rgba(255, 86, 48, 0.32)',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      color: '#B71D18',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        bgcolor: 'rgba(255, 86, 48, 0.12)',
                        borderColor: '#FF5630',
                      },
                    }}
                    title={`Go to: ${error.cardTitle}`}
                  >
                    {error.label}
                    <Iconify icon="mdi:arrow-right" width={14} height={14} />
                  </Box>
                ))}
                {validationErrors.length > 5 && (
                  <Typography sx={{ fontSize: '0.75rem', color: '#B71D18', alignSelf: 'center', ml: 0.5 }}>
                    +{validationErrors.length - 5} more
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          <StickyFooter
            onBack={handleBack}
            onSkip={isOnClosureSection ? undefined : handleSkip}
            onSave={handleSave}
            onNext={isOnClosureSection || (hasCloseCallAttribute && currentCardIndex === cards.length - 1) ? undefined : handleNext}
            isBackDisabled={currentCardIndex === 0 && !isOnClosureSection}
            isComplete={false}
            nextLabel="Continue"
            showSkip={!isOnClosureSection && !(hasCloseCallAttribute && currentCardIndex === cards.length - 1)}
            showKeyboardHint={!isOnClosureSection && !(hasCloseCallAttribute && currentCardIndex === cards.length - 1)}
            saveAdornment={hintToggleChips}
          />
              </Box>
            </Fade>
          )}
        </Box>

        <ContextPanel
          type={type}
          scriptCategory={scriptCategory}
          patient={patientData}
          tags={leadData.tags || []}
          progress={progressData}
          collectedData={collectedData}
          notes={callNotes}
          onSectionClick={handleSectionClick}
          onQuestionClick={(cardIndex) => {
            setCurrentCardIndex(cardIndex);
            if (scrollableView) {
              setTimeout(() => {
                const cardRef = scrollableCardRefs.current[cardIndex];
                if (cardRef) {
                  cardRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }, 50);
            }
          }}
          cards={cards}
          currentCardIndex={currentCardIndex}
          completedCards={completedCards}
          skippedCards={skippedCards}
          isOpen={contextPanelOpen}
          mergeTagData={mergeTagData}
          conditionalLogics={conditionalLogics}
          showConditionalIndicators={previewMode}
        />
      </Box>

      {/* Treatment Switching Modal - Shows when switching to treatment-specific script */}
      <Backdrop
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(33, 43, 54, 0.8)',
        }}
        open={treatmentSwitchingModal.open}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            p: 4,
            borderRadius: '16px',
            backgroundColor: '#fff',
            boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.25)',
            minWidth: 320,
            maxWidth: 400,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: 'rgba(0, 167, 111, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={32} thickness={4} sx={{ color: '#00A76F' }} />
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: '1.125rem',
                fontWeight: 700,
                color: '#212B36',
                mb: 0.5,
              }}
            >
              Loading Script
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#637381',
                maxWidth: 280,
              }}
            >
              {treatmentSwitchingModal.message || 'Loading treatment-specific script...'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: '8px',
              bgcolor: 'rgba(0, 167, 111, 0.08)',
            }}
          >
            <Iconify icon="mdi:check-circle" width={16} height={16} sx={{ color: '#00A76F' }} />
            <Typography sx={{ fontSize: '0.75rem', color: '#00A76F', fontWeight: 500 }}>
              Your form data will be preserved
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      {/* AI Summary Modal - Rendered outside main content for proper layering */}
      <FeatureGuard feature="ai-call-summary" level="view">
        <AISummaryModal
          open={aiSummaryModalOpen}
          onBackToScript={handleBackToScript}
          onCloseAll={handleCloseAll}
          isLoading={aiSummaryLoading}
          summaryData={aiSummaryData}
          leadName={followLead ? `${followLead.first_name || ''} ${followLead.last_name || ''}`.trim() : 'Patient'}
          leadInitials={followLead ? `${(followLead.first_name || '')[0] || ''}${(followLead.last_name || '')[0] || ''}`.toUpperCase() : '?'}
          callType={type}
          callStartTime={callStartTime}
          callDuration={callDuration}
          dataCompleteness={dataCompleteness}
          formData={formData}
          cardNotes={cardNotes}
          cards={cards}
          onEditSkippedQuestion={(cardIndex) => {
            // Close modal and navigate to the skipped card
            setAiSummaryModalOpen(false);
            setCurrentCardIndex(cardIndex);
          }}
          onBookAppointment={handleOpenAIBookingWizard}
          onAddReminder={handleAddReminder}
          onSendWhatsApp={handleSendWhatsApp}
          appointmentBooked={appointmentBooked}
          bookedAppointmentData={bookedAppointmentData}
          leadInfo={followLead ? {
            id: followLead.id,
            first_name: followLead.first_name,
            last_name: followLead.last_name,
            phone: followLead.lead_contact?.phone || '',
            email: followLead.lead_contact?.email || '',
          } : null}
          whatsappTemplates={whatsappTemplates || []}
          emailTemplates={emailTemplates?.results || []}
          onSaveTags={handleSaveTags}
          onSaveAINotes={handleSaveAINotes}
        />
      </FeatureGuard>

      {/* AI Booking Wizard - Pre-filled appointment booking based on AI analysis */}
      <FeatureGuard feature="ai-call-summary" level="view">
        <AIBookingWizard
          open={aiBookingWizardOpen}
          onClose={handleCloseAIBookingWizard}
          onBook={handleAIBookAppointment}
          onTreatmentChange={(treatmentData) => {
            // Save treatment changes to lead using the existing saveTreatmentChanges function
            // Note: We don't pass treatment_id here because treatmentData.treatmentId is the
            // practice treatment catalog ID, not the lead_treatment ID. saveTreatmentChanges
            // will correctly use formData.treatment_id or followLead.lead_treatments[0].id
            saveTreatmentChanges({
              treatment: treatmentData.treatment,
              treatmentValue: treatmentData.treatmentPrice,
              teethAmount: treatmentData.teethAmount,
            });
            // Update formData so call script stays in sync
            // Keep the existing treatment_id (lead_treatment ID) - don't overwrite with practice treatment ID
            setFormData((prev) => ({
              ...prev,
              treatment: treatmentData.treatment,
              treatmentValue: treatmentData.treatmentPrice,
              teethAmount: treatmentData.teethAmount,
            }));
          }}
          aiRecommendation={aiSummaryData?.bookAppointment}
          availability={aiSummaryData?.availability}
          leadInfo={followLead}
          treatments={followLead?.lead_treatments || []}
          practiceUsers={practiceUser?.results || []}
          currentUser={currentAuthUser}
          isBooking={aiBookingLoading}
        />
      </FeatureGuard>

      {/* Wrong Person Modal - Shows when user selects "Wrong person" in confirm_speaker */}
      <Dialog
        open={showWrongPersonModal}
        onClose={handleWrongPersonCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          },
        }}
      >
        {/* Header with gradient background */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #FF5630 0%, #B71D18 100%)',
            px: 3,
            py: 3,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '16px',
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Iconify icon="solar:user-cross-bold" width={32} height={32} sx={{ color: 'white' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
            Wrong Person
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.8)' }}>
            This call reached the wrong contact
          </Typography>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {/* Auto-note info box */}
          <Box
            sx={{
              p: 2,
              mb: 3,
              borderRadius: '12px',
              bgcolor: '#FFF5F2',
              border: '1px solid #FFCDC2',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Iconify icon="mdi:note-plus" width={18} height={18} sx={{ color: '#FF5630' }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#B71D18', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Auto-generated note
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.9rem', color: '#7A0916', fontWeight: 500 }}>
              "Wrong phone number linked to lead"
            </Typography>
          </Box>

          {/* Additional notes field */}
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#637381', mb: 1 }}>
            Additional Notes (Optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Add any additional context about this call..."
            value={wrongPersonNote}
            onChange={(e) => setWrongPersonNote(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: '#F9FAFB',
                '&:hover': {
                  bgcolor: '#F4F6F8',
                },
                '&.Mui-focused': {
                  bgcolor: 'white',
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E5E8EB',
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0, gap: 1.5 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleWrongPersonCancel}
            sx={{
              borderRadius: '10px',
              py: 1.25,
              fontWeight: 600,
              borderColor: '#DFE3E8',
              color: '#637381',
              '&:hover': {
                borderColor: '#919EAB',
                bgcolor: '#F9FAFB',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleWrongPersonSubmit}
            sx={{
              borderRadius: '10px',
              py: 1.25,
              fontWeight: 600,
              bgcolor: '#FF5630',
              boxShadow: '0 8px 16px rgba(255, 86, 48, 0.24)',
              '&:hover': {
                bgcolor: '#B71D18',
                boxShadow: '0 8px 16px rgba(183, 29, 24, 0.24)',
              },
            }}
          >
            Save & Close Call
          </Button>
        </DialogActions>
      </Dialog>

      {/* Per-question Send Email composer (live-call only). One shared instance — every
          question's email icon opens this with the current lead context. Mounted whenever
          not in preview so the dialog is available even before a lead has been identified
          (agent can type the recipient manually). Props accept null leadId / leadDetail. */}
      {!previewMode && (
        <IosEmailComposer
          open={emailComposerOpen}
          onClose={() => setEmailComposerOpen(false)}
          leadId={followLead?.id || formData?.lead_id || null}
          practiceId={practiceId}
          leadDetail={followLead || null}
        />
      )}

      {/* Per-question Send WhatsApp composer (live-call only). Shared instance, same
          lead context across all question cards. Also mounted pre-lead-identification so
          the agent can reach a patient manually before the script has a lead_id. */}
      {!previewMode && (
        <IosWhatsAppComposerDialog
          open={whatsappComposerOpen}
          onClose={() => setWhatsappComposerOpen(false)}
          leadId={followLead?.id || formData?.lead_id || null}
          practiceId={practiceId}
          phoneNumber={formData?.phone_number || followLead?.lead_contact?.phone || ''}
          leadDetail={followLead || null}
        />
      )}

    </Box>
  );
};

CallScriptV2.propTypes = {
  scriptData: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    sections: PropTypes.array,
    cards: PropTypes.array,
  }),
  leadData: PropTypes.shape({
    name: PropTypes.string,
    initials: PropTypes.string,
    tags: PropTypes.array,
  }),
  followLead: PropTypes.object,
  type: PropTypes.oneOf(['inbound', 'outbound']),
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  onComplete: PropTypes.func,
  onAppointmentBooked: PropTypes.func,
  onAppointmentNotBooked: PropTypes.func,
  onUnableToContact: PropTypes.func,
  previewMode: PropTypes.bool,
  scriptCategory: PropTypes.oneOf(['INBOUND', 'OUTBOUND', 'TCO']),
  onCardChange: PropTypes.func,
  navigateTo: PropTypes.shape({
    sectionIndex: PropTypes.number,
    questionIndex: PropTypes.number,
  }),
  scrollableView: PropTypes.bool,
  // Editor-only: when set, Preview filters questions as if this treatment is the lead's interest.
  // `name` is the treatment_item.name, `category` is the treatment_category.name (or null).
  simulatedTreatment: PropTypes.shape({
    name: PropTypes.string.isRequired,
    category: PropTypes.string,
  }),
  // Callback fired when the user clicks the banner's clear (X). Setter lives at the parent.
  onClearSimulatedTreatment: PropTypes.func,
};

export default CallScriptV2;
